// ══════════════════════════════════════════════════════
// Strategy Lab — Detail Views & Run Cards
// ══════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from "react";
import { theme, fonts } from "../../theme";
import { Card, CardHeader, Pill, Button, Metric, SectionTitle, formatPnl, pnlColor } from "../../components/ui";
import { SkeletonCards, SortTh, sortRows, downloadCSV } from "../../components/shared";
import { getApi, fDate, bestParamsToApplyData } from "./constants";
import { EquityChart, DrawdownChart, MonthlyHeatmap, TrialChart, WFBars, ParetoChart } from "./Charts";
import TradeVisualizer from "../../components/TradeVisualizer";
import TradeAnalysisPanel from "../../components/TradeAnalysisPanel";
import TradeAggregatePanel from "../../components/TradeAggregatePanel";
import { RegimeTab } from "./RegimeTab";

/* ── Run Card (reusable) ─────────────────────── */
export function RunCard({ run, onClick, onCompare, onHide, onDelete }) {
  // Sprint K: WF-Obj PnL-Semantik klären
  // WF-Obj net_pnl = avg pro OOS-Fenster (für Bayesian-Score-Basis)
  // wf_obj_extrapolated_pnl = lineare Hochrechnung auf volle Periode (vergleichbar mit Standard-IS-Total)
  // Im Card zeigen wir bei WF-Obj die Extrapolation, weil das die User-Erwartung trifft.
  // Sprint K Frontend Fix: Detect WF-Obj aus Trial-Daten (mode-marker fehlt im Backend-Status)
  const isWfObj = (run.optimization_mode || run.goals?.optimization_mode) === "wf_objective"
    || (run.trial_history || []).some(t => t.wf_obj_extrapolated_pnl != null)
    || run.best_wf_obj_extrapolated_pnl != null
    || run.wf_obj_extrapolated_pnl != null;
  const wfObjExtrap = run.wf_obj_extrapolated_pnl ?? run.best_wf_obj_extrapolated_pnl;
  const pnl = isWfObj && wfObjExtrap != null
    ? wfObjExtrap
    : (run.net_pnl ?? run.best_net_pnl ?? run.total_oos_pnl ?? 0);
  const up = pnl > 0;
  // v4-FIX: WF runs don't have a top-level trades count; aggregate from windows if available
  let trades = run.total_trades || run.trades || run.best_trades || 0;
  if (!trades && Array.isArray(run.windows)) {
    trades = run.windows.reduce((s, w) => s + (w.oos_trades || w.total_trades || 0), 0);
  }

  // v4-FIX: Classify run type for consistent cross-card labeling
  const runType = Array.isArray(run.windows) || run.total_oos_pnl != null || run.robustness
    ? "walkforward"
    : (run.trials_completed != null || run.mode === "tuning" || run.best_score != null)
      ? "optimizer"
      : "backtest";
  const typeLabel = { walkforward: "Walk-Forward", optimizer: "Optimizer", backtest: "Backtest" }[runType];
  const typeColor = { walkforward: theme.amberText, optimizer: theme.purpleText, backtest: theme.blueText }[runType];
  const typeBg = { walkforward: theme.amberLight, optimizer: theme.purpleLight, backtest: theme.blueLight }[runType];

  // v4: single mode only
  const modeInfo = {
    tuning: { variant: null, phase: "Tuning", color: "#059669" },
  };
  const info = modeInfo[run.mode];
  const phaseLabel = info ? info.phase : (run.mode || null);
  const phaseColor = info?.color || theme.blue;

  // v4-FIX: Consistent asset+strategy display with fallback chain
  const assetName = run.instrument || run.symbol || run.name || "—";
  // v5-FIX: Check multiple possible locations for strategy_file (varies by endpoint)
  const stratName = run.strategy_file
    || run.strategy_name
    || run.params?.strategy_file
    || run.summary?.strategy_file
    || run.config?.strategy_file
    || null;

  // v5-FEAT: Detect if strategy_file is an experiment
  // Experiments live in strategies/experiments/ OR follow naming convention {base}__{name}.yaml
  const isExperiment = stratName && (
    stratName.includes("experiments/") ||
    stratName.includes("__")
  );
  // Extract clean experiment name for display (if present)
  // Handles both "experiments/base__name.yaml" and "base__name.yaml"
  const expDisplayName = isExperiment && stratName
    ? (() => {
        const base = (stratName.split("/").pop() || "").replace(/\.yaml$/, "");
        const parts = base.split("__");
        // Remove empty and duplicate segments (handles bug: base__base__name)
        const unique = parts.filter((p, i) => p && parts.indexOf(p) === i);
        return unique.slice(1).join("__") || null;
      })()
    : null;

  // Run Config classification (Sprint B/C/D)
  const optMode = run.optimization_mode || run.goals?.optimization_mode || "standard";
  const optModeInfo = {
    wf_objective: { label:"WF-Obj", color:"#2563eb", bg:"rgba(37,99,235,0.1)" },
    pareto:       { label:"Pareto", color:"#d97706", bg:"rgba(217,119,6,0.1)"  },
    standard:     { label:"Standard", color:"#6b7280", bg:"rgba(107,114,128,0.08)" },
  };
  const optModeDisplay = optModeInfo[optMode] || optModeInfo.standard;
  const stabCheck = run.goals?.stability_check?.enabled || run.stability_check?.enabled || false;

  // Sprint Profile-System (2026-05-20): Profile-Badge
  const optProfile = run.optimizer_profile || run.goals?.optimizer_profile || null;
  const profileInfo = {
    conservative: { label:"🛡 Konservativ", color:"#2563eb", bg:"rgba(37,99,235,0.08)" },
    moderate:     { label:"⚖ Moderat",     color:"#059669", bg:"rgba(16,185,129,0.08)" },
    aggressive:   { label:"🚀 Aggressiv",   color:"#d97706", bg:"rgba(217,119,6,0.08)" },
  };
  const profileDisplay = optProfile ? profileInfo[optProfile] : null;

  return <Card className="sl-card" style={{
    cursor:onClick?"pointer":"default",
    ...(isExperiment ? { border: `1.5px dashed ${theme.amber || "#f59e0b"}` } : {}),
  }} onClick={onClick}>
    <div style={{ height:3, background:up?`linear-gradient(90deg,${theme.greenBright},transparent)`:`linear-gradient(90deg,${theme.red},transparent)` }}/>
    <div style={{ padding:"14px 18px" }}>
      {/* ── HEADER: Type-Badge + Asset + Actions ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8, gap:8 }}>
        <div style={{ minWidth:0, flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexWrap:"wrap" }}>
            <Pill bg={typeBg} color={typeColor}>{typeLabel}</Pill>
            <span style={{ fontSize:15, fontWeight:800 }}>{String(assetName)}</span>
            {phaseLabel && <Pill bg={`${phaseColor}15`} color={phaseColor}>{phaseLabel}</Pill>}
            {runType === "optimizer" && <Pill bg={optModeDisplay.bg} color={optModeDisplay.color}>{optModeDisplay.label}</Pill>}
            {runType === "optimizer" && profileDisplay && <Pill bg={profileDisplay.bg} color={profileDisplay.color}>{profileDisplay.label}</Pill>}
            {runType === "optimizer" && stabCheck && <Pill bg="rgba(16,185,129,0.1)" color="#059669">🧪 Stabil.</Pill>}
            {isExperiment && <Pill bg={theme.amberLight || "#fef3c7"} color={theme.amberText || "#92400e"}>
              🧪 {expDisplayName ? `EXP: ${expDisplayName}` : "EXPERIMENT"}
            </Pill>}
          </div>
          {/* Strategy-File (immer angezeigt wenn vorhanden) */}
          {stratName && <div style={{ fontSize:11, color:theme.text2, fontFamily:fonts.mono, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={stratName}>
            📄 {String(stratName)}
          </div>}
          <div style={{ fontSize:10, color:theme.text3, fontFamily:fonts.mono, marginTop:2 }}>
            {fDate(run.timestamp||run.created_at)} · {String(trades)} trades
            {run.trials_completed && <span> · {run.trials_completed} trials</span>}
            {runType === "walkforward" && run.windows && Array.isArray(run.windows) && <span> · {run.windows.length} windows</span>}
          </div>
          {/* Optimizer: show which trial the metrics come from */}
          {runType === "optimizer" && (run.best_composite_trial || run.best_score_trial) && (
            <div style={{ fontSize:10, color:theme.text3, fontFamily:fonts.mono, marginTop:3 }}>
              {run.best_composite_trial && run.best_score_trial && run.best_composite_trial !== run.best_score_trial
                ? <span>Angezeigt: Trial #{run.best_composite_trial} (PnL+Sharpe) · Score-Best: #{run.best_score_trial}</span>
                : <span>Best Trial: #{run.best_composite_trial || run.best_score_trial}</span>
              }
            </div>
          )}
          {/* WF: show OOS constraints that were applied */}
          {runType === "walkforward" && run.oos_constraints && (
            <div style={{ marginTop:5, display:"flex", flexWrap:"wrap", gap:4 }}>
              {run.oos_constraints.min_total_oos_pnl != null && (
                <span style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:theme.greenLight, color:theme.greenText, fontFamily:fonts.mono }}>
                  OOS PnL ≥ ${run.oos_constraints.min_total_oos_pnl}
                </span>
              )}
              {run.oos_constraints.min_profitable_windows != null && (
                <span style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:theme.blueLight, color:theme.blueText, fontFamily:fonts.mono }}>
                  Win-Windows ≥ {run.oos_constraints.min_profitable_windows}%
                </span>
              )}
              {run.oos_constraints.max_total_drawdown != null && (
                <span style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:theme.redLight, color:theme.redText, fontFamily:fonts.mono }}>
                  Max DD ≤ {run.oos_constraints.max_total_drawdown}%
                </span>
              )}
              {run.oos_constraints.min_profit_factor != null && (
                <span style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:theme.tint, color:theme.text3, fontFamily:fonts.mono }}>
                  Min PF {run.oos_constraints.min_profit_factor}
                </span>
              )}
            </div>
          )}

          {(run.entry_tf||run.context_tf||run.regime_tf) && <div style={{ fontSize:10, color:theme.blueText, fontFamily:fonts.mono, marginTop:4, display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
            <span style={{ padding:"2px 7px", borderRadius:4, background:theme.blueLight, fontWeight:700, letterSpacing:"0.3px" }}>
              {run.entry_tf && <span>E:{run.entry_tf}</span>}
              {run.entry_tf && run.context_tf && <span style={{opacity:0.4, margin:"0 3px"}}>/</span>}
              {run.context_tf && <span>C:{run.context_tf}</span>}
              {run.context_tf && run.regime_tf && <span style={{opacity:0.4, margin:"0 3px"}}>/</span>}
              {run.regime_tf && <span>R:{run.regime_tf}</span>}
            </span>
          </div>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
          {onCompare && run.id && <button onClick={e => { e.stopPropagation(); onCompare(run.id); }} style={{ fontSize:9, padding:"2px 7px", borderRadius:4, border:`1px solid ${theme.border}`, background:theme.white, color:theme.text3, cursor:"pointer" }} title="Compare">vs</button>}
          {onDelete && run.id && <button onClick={e => { e.stopPropagation(); onDelete(run.id); }} style={{ fontSize:9, padding:"2px 7px", borderRadius:4, border:`1px solid rgba(239,68,68,0.2)`, background:"rgba(239,68,68,0.04)", color:theme.redText, cursor:"pointer" }} title="Löschen">🗑</button>}
          {!onDelete && onHide && run.id && <button onClick={e => { e.stopPropagation(); onHide(run.id); }} style={{ fontSize:9, padding:"2px 7px", borderRadius:4, border:`1px solid ${theme.border}`, background:theme.white, color:theme.text4, cursor:"pointer" }} title="Ausblenden">✕</button>}
          {run.id && onClick && <span style={{ fontSize:9, color:theme.text4, fontFamily:fonts.mono }}>#{run.id}</span>}
          <Pill bg={up?theme.greenLight:theme.redLight} color={up?theme.greenText:theme.redText}>{run.status==="running"?"Running":up?"Profit":"Loss"}</Pill>
        </div>
      </div>
      <div style={{ fontSize:26, fontWeight:900, fontFamily:fonts.mono, color:up?theme.greenText:theme.redText, letterSpacing:"-1.5px", marginBottom:10 }}>{formatPnl(pnl)}</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6, paddingTop:10, borderTop:`1px solid ${theme.divider}` }}>
        {run.win_rate!=null && <Metric label="Win Rate" value={`${(Number(run.win_rate)>1?Number(run.win_rate):(Number(run.win_rate)*100)).toFixed(1)}%`} color={(Number(run.win_rate)>1?run.win_rate/100:run.win_rate)>0.55?theme.greenText:theme.redText} small/>}
        {run.profit_factor!=null && <Metric label="PF" value={Number(run.profit_factor).toFixed(2)} color={run.profit_factor>1.3?theme.greenText:theme.text} small/>}
        {run.sharpe!=null && <Metric label="Sharpe" value={Number(run.sharpe).toFixed(2)} color={run.sharpe>1.5?theme.greenText:theme.text3} small/>}
        {(run.max_drawdown??run.max_dd_pct)!=null && <Metric label="Max DD" value={`${Number(run.max_drawdown||run.max_dd_pct).toFixed(1)}%`} color={theme.redText} small/>}
        <Metric label="Trades" value={String(trades)} small/>
        {run.robustness && <Metric label="Robust." value={String(run.robustness)} color={run.robustness==="ROBUST"?theme.greenText:theme.redText} small/>}
        {run.best_label && <Metric label="Best" value={String(run.best_label)} small/>}
        {run.best_score!=null && <Metric label="B-Score" value={Number(run.best_score).toFixed(2)} color={theme.text3} small/>}
      </div>
    </div>
  </Card>;
}

/* ── BackButton ──────────────────────────────── */
function BackBtn({ onClick }) {
  return <button onClick={onClick} className="sl-btn" style={{ width:32, height:32, borderRadius:8, border:`1px solid ${theme.border}`, background:theme.white, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", color:theme.text3 }}>←</button>;
}

/* ═══ BT DETAIL ═══════════════════════════════════ */
export function BtDetail({ runId, onClose }) {
  const [data,setData] = useState(null); const [ld,setLd] = useState(true); const [tab,setTab] = useState("overview");
  const [page,setPage] = useState(0); const PAGE = 50;
  const [sort,_setSort] = useState({ key:null, asc:false });
  const [vizState, setVizState] = useState({ open: false, index: 0 });
  const setSort = useCallback(s => { _setSort(s); setPage(0); }, []);
  useEffect(() => { if (!runId) return; setLd(true); getApi(`/results/backtests/${runId}`).then(d => { setData(d); setLd(false); }); }, [runId]);
  if (ld) return <SkeletonCards n={6}/>;
  if (!data || data.detail) return <Card style={{ padding:32, textAlign:"center" }}><div style={{ color:theme.redText, fontSize:13 }}>Run not found</div><Button onClick={onClose} style={{ marginTop:12 }}>Back</Button></Card>;
  const up = (data.net_pnl||0) >= 0, allTrades = data.trades||[], q = data.quality||{};
  const sorted = sortRows(allTrades, sort);
  const trades = sorted.slice(page*PAGE, (page+1)*PAGE);
  const totalPages = Math.ceil(sorted.length/PAGE);

  return <div className="sl-fadein">
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <BackBtn onClick={onClose}/>
        <div>
          <div style={{ fontSize:18, fontWeight:900 }}>{data.instrument} <span style={{ fontSize:12, fontWeight:500, color:theme.text3 }}>#{runId}</span></div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:3 }}>
            <span style={{ fontSize:12, color:theme.text3, fontFamily:fonts.mono }}>{data.period_start} → {data.period_end}</span>
            {(data.entry_tf||data.timeframes) && <Pill bg={theme.blueLight} color={theme.blueText}>
              {data.entry_tf || (data.timeframes ? `${data.timeframes.entry}/${data.timeframes.context}/${data.timeframes.regime}` : "")}
            </Pill>}
            {data.strategy_file && <span style={{ fontSize:11, color:theme.text4, fontFamily:fonts.mono }}>{data.strategy_file}</span>}
          </div>
        </div>
      </div>
      <div style={{ textAlign:"right" }}>
        <div style={{ fontSize:28, fontWeight:900, fontFamily:fonts.mono, color:up?theme.greenText:theme.redText, letterSpacing:"-1.5px" }}>{formatPnl(data.net_pnl)}</div>
        <div style={{ display:"flex", gap:5, justifyContent:"flex-end", marginTop:4 }}>
          {q.grade && <Pill bg={q.grade==="A"?theme.greenLight:q.grade==="B"?theme.blueLight:theme.amberLight} color={q.grade==="A"?theme.greenText:q.grade==="B"?theme.blueText:theme.amberText}>Grade {q.grade}</Pill>}
          {q.composite_score!=null && <Pill bg={theme.tint} color={theme.text2}>Score {Number(q.composite_score).toFixed(2)}</Pill>}
        </div>
      </div>
    </div>

    {/* ── Analysis & Interpretation ────────── */}
    {(() => {
      const wr = Number(data.win_rate)||0; const wrPct = wr > 1 ? wr : wr*100;
      const pf = Number(data.profit_factor)||0; const sh = Number(data.sharpe)||0;
      const dd = Number(data.max_drawdown)||0; const trades = data.total_trades||0;
      const exp = Number(data.expectancy)||0;
      const exitR = data.exit_reason_distribution||{};
      const slPct = exitR.SL ? ((exitR.SL.count/trades)*100).toFixed(0) : null;
      const bePct = exitR.BE ? ((exitR.BE.count/trades)*100).toFixed(0) : null;
      const trailPct = exitR.Trail ? ((exitR.Trail.count/trades)*100).toFixed(0) : null;
      const monthly = data.monthly_returns||{};
      const months = Object.entries(monthly);
      const negMonths = months.filter(([,v])=>v<0).length;
      const posMonths = months.filter(([,v])=>v>0).length;
      const insights = [];
      // P&L Assessment
      if (up && pf > 1.5 && sh > 2.0) insights.push({icon:"✅",text:`Starke Basis: P&L ${formatPnl(data.net_pnl)} mit PF ${pf.toFixed(2)} und Sharpe ${sh.toFixed(2)} — guter Ausgangspunkt für Optimierung.`,color:theme.greenText});
      else if (up && pf > 1.2) insights.push({icon:"⚠️",text:`Profitabel (${formatPnl(data.net_pnl)}), aber PF ${pf.toFixed(2)} und Sharpe ${sh.toFixed(2)} sind noch ausbaufähig. Die Optimierung kann hier deutlich verbessern.`,color:theme.amberText});
      else if (!up) insights.push({icon:"❌",text:`Nicht profitabel mit Standardparametern. Das ist normal — die Pipeline-Optimierung (Phase 1–3) kann die Parameter signifikant verbessern.`,color:theme.redText});
      // Win Rate
      if (wrPct < 40) insights.push({icon:"📊",text:`Win-Rate ${wrPct.toFixed(1)}% ist niedrig, wird aber durch hohes RR (Avg RR ${(data.avg_rr||0).toFixed(2)}) kompensiert. Das ist typisch für Trend-Following.`});
      else if (wrPct > 55) insights.push({icon:"📊",text:`Win-Rate ${wrPct.toFixed(1)}% ist überdurchschnittlich — achte darauf, dass Gewinn-Trades groß genug sind (Avg RR ${(data.avg_rr||0).toFixed(2)}).`});
      // Drawdown
      if (dd > 10) insights.push({icon:"🛡️",text:`Drawdown ${dd.toFixed(1)}% ist hoch. Phase 3 wird Risk-Parameter (SL, Position-Sizing) optimieren um das zu reduzieren.`,color:theme.redText});
      else if (dd < 5) insights.push({icon:"🛡️",text:`Drawdown ${dd.toFixed(1)}% ist sehr kontrolliert — gutes Risikomanagement.`,color:theme.greenText});
      // Exit Reasons
      if (slPct && Number(slPct) > 30) insights.push({icon:"🚪",text:`${slPct}% aller Trades enden im SL. Phase 2 testet ob Break-Even und Trailing die SL-Quote senken können.`});
      if (bePct && Number(bePct) > 30) insights.push({icon:"⚖️",text:`${bePct}% Break-Even Exits — viele Trades schaffen es nicht zu TP1. Möglicherweise ist der BE-Trigger zu aggressiv.`});
      // Monthly Consistency
      if (months.length > 6) insights.push({icon:"📅",text:`${posMonths} profitable vs. ${negMonths} negative Monate. ${posMonths>negMonths*2?"Gute Konsistenz.":posMonths>negMonths?"Akzeptable Konsistenz.":"Inkonsistente Monthly Returns — Optimierung priorisieren."}`});
      // Trade Count
      if (trades < 100) insights.push({icon:"⚠️",text:`Nur ${trades} Trades — statistische Aussagekraft ist begrenzt. Erwäge einen längeren Zeitraum.`,color:theme.amberText});

      if (insights.length === 0) return null;
      return <Card style={{ marginBottom:18, padding:"16px 20px", background:"rgba(37,99,235,0.02)", border:`1px solid rgba(37,99,235,0.08)` }} className="sl-fadein">
        <div style={{ fontSize:14, fontWeight:800, color:theme.text, marginBottom:10 }}>📋 Analyse & Interpretation</div>
        {insights.map((ins,i) => <div key={i} style={{ display:"flex", gap:10, padding:"6px 0", borderBottom:i<insights.length-1?`1px solid ${theme.divider}`:"none" }}>
          <span style={{ fontSize:14, flexShrink:0 }}>{ins.icon}</span>
          <span style={{ fontSize:13, color:ins.color||theme.text2, lineHeight:1.6 }}>{ins.text}</span>
        </div>)}
      </Card>;
    })()}

    {/* Quality Dimensions */}
    {q.dimensions && <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:18 }}>
      {[["Profitability",q.dimensions.profitability],["Risk Control",q.dimensions.risk_control],["Consistency",q.dimensions.consistency],["Reliability",q.dimensions.reliability]].map(([n,v]) =>
        <Card key={n} className="sl-card" style={{ padding:"10px 14px" }}>
          <div style={{ fontSize:8.5, fontWeight:700, color:theme.text4, textTransform:"uppercase", letterSpacing:".6px", marginBottom:5 }}>{n}</div>
          <div style={{ height:5, borderRadius:3, background:theme.tint, marginBottom:5 }}><div style={{ height:"100%", borderRadius:3, background:v>0.7?theme.green:v>0.4?theme.amber:theme.red, width:`${(v||0)*100}%`, transition:"width 0.3s" }}/></div>
          <div style={{ fontSize:13, fontWeight:800, fontFamily:fonts.mono, color:v>0.7?theme.greenText:v>0.4?theme.amberText:theme.redText }}>{((v||0)*100).toFixed(0)}%</div>
        </Card>
      )}
    </div>}

    {/* Tabs */}
    <div style={{ display:"flex", gap:3, padding:3, borderRadius:8, background:theme.tint, marginBottom:18, width:"fit-content" }}>
      {["overview","equity","analysis","trades","distribution","scenarios",...(data.monte_carlo?["monte_carlo"]:[]),...(data.synthetic_bootstrap?["synthetic_bootstrap"]:[]),...(data.bar_bootstrap?["bar_bootstrap"]:[])].map(t =>
        <button key={t} onClick={() => { setTab(t); setPage(0); setSort({key:null,asc:false}); }} className="sl-btn" style={{ padding:"5px 13px", borderRadius:6, border:"none", cursor:"pointer", fontSize:11, fontWeight:tab===t?700:500, background:tab===t?theme.white:"transparent", color:tab===t?theme.text:theme.text3, boxShadow:tab===t?theme.shadow1:"none", fontFamily:fonts.body, textTransform:"capitalize" }}>{t==="scenarios"?"🎯 Scenarios":t==="analysis"?"📊 Analysis":t==="monte_carlo"?"🎲 Monte-Carlo":t==="synthetic_bootstrap"?"🔀 Bootstrap":t==="bar_bootstrap"?"📊 Bar-Bootstrap":t}</button>
      )}
    </div>

    {tab==="overview" && <>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(125px,1fr))", gap:8, marginBottom:22 }}>
        {[["Net P&L",formatPnl(data.net_pnl),up?theme.greenText:theme.redText],["Win Rate",`${(Number(data.win_rate)>1?Number(data.win_rate):(Number(data.win_rate)*100)).toFixed(1)}%`,(Number(data.win_rate)>1?data.win_rate/100:data.win_rate)>0.55?theme.greenText:theme.text],["Profit Factor",Number(data.profit_factor).toFixed(2),data.profit_factor>1.3?theme.greenText:theme.text],["Sharpe",Number(data.sharpe).toFixed(2),data.sharpe>1.5?theme.greenText:theme.text3],["Sortino",data.sortino!=null?Number(data.sortino).toFixed(2):"—",theme.text3],["Max DD %",`${Number(data.max_drawdown).toFixed(1)}%`,theme.redText],["Max DD $",formatPnl(-(data.max_drawdown_usd||0)),theme.redText],["Trades",data.total_trades,theme.text],["Winners",data.winners,theme.greenText],["Losers",data.losers,theme.redText],["Avg Win",formatPnl(data.avg_win),theme.greenText],["Avg Loss",formatPnl(data.avg_loss),theme.redText],["Expectancy",formatPnl(data.expectancy),data.expectancy>0?theme.greenText:theme.redText],["Avg RR",data.avg_rr!=null?Number(data.avg_rr).toFixed(2):"—",theme.text],["Largest Win",formatPnl(data.largest_win),theme.greenText],["Largest Loss",formatPnl(data.largest_loss),theme.redText],["Commission",formatPnl(-(data.total_commission||0)),theme.redText],["Slippage",formatPnl(-(data.total_slippage||0)),theme.redText]].map(([l,v,c]) =>
          <Card key={l} className="sl-card" style={{ padding:"10px 12px" }}>
            <div style={{ fontSize:8.5, fontWeight:700, color:theme.text4, textTransform:"uppercase", letterSpacing:".6px", marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:14, fontWeight:800, fontFamily:fonts.mono, color:c, lineHeight:1 }}>{v}</div>
          </Card>
        )}
      </div>
      <SectionTitle>Monthly Returns</SectionTitle>
      <Card style={{ padding:"12px 16px", marginBottom:22 }}><MonthlyHeatmap data={data.monthly_returns}/></Card>
      {data.exit_reason_distribution && Object.keys(data.exit_reason_distribution).length > 0 && <>
        <SectionTitle>Exit Reasons</SectionTitle>
        <Card style={{ marginBottom:22 }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ borderBottom:`1px solid ${theme.divider}` }}>{["Reason","Count","P&L","% Trades"].map(h => <th key={h} style={{ padding:"7px 14px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>{h}</th>)}</tr></thead>
            <tbody>{Object.entries(data.exit_reason_distribution).sort(([,a],[,b]) => b.count-a.count).map(([r,d]) =>
              <tr key={r} className="sl-row" style={{ borderBottom:`1px solid ${theme.divider}` }}>
                <td style={{ padding:"6px 14px", fontSize:12, fontWeight:600, fontFamily:fonts.mono }}>{r}</td>
                <td style={{ padding:"6px 14px", fontSize:12, fontFamily:fonts.mono }}>{d.count}</td>
                <td style={{ padding:"6px 14px", fontSize:12, fontWeight:700, fontFamily:fonts.mono, color:d.pnl>=0?theme.greenText:theme.redText }}>{formatPnl(d.pnl)}</td>
                <td style={{ padding:"6px 14px", fontSize:12, fontFamily:fonts.mono }}>{data.total_trades?`${((d.count/data.total_trades)*100).toFixed(1)}%`:"—"}</td>
              </tr>
            )}</tbody>
          </table>
        </Card>
      </>}
    </>}
    {tab==="equity" && <>
      <SectionTitle>Equity Curve</SectionTitle>
      <Card style={{ padding:"12px 16px", marginBottom:18 }}><EquityChart data={data.equity_curve}/></Card>
      <SectionTitle>Drawdown</SectionTitle>
      <Card style={{ padding:"12px 16px" }}><DrawdownChart data={data.drawdown_series}/></Card>
    </>}
    {tab==="analysis" && <Card>
      <CardHeader title="📊 Trade Analysis" sub="Summary · Equity-Kurve · Exit-Reasons"/>
      <div style={{ padding:"14px 20px" }}>
        <TradeAnalysisPanel runType="bt" runId={runId}/>
      </div>
    </Card>}

    {tab==="trades" && <Card>
      <CardHeader title="Trades" sub={`${allTrades.length} total`} right={<Button onClick={() => downloadCSV(allTrades,`trades_${data.instrument}_${runId}.csv`)}>CSV Export</Button>}/>
      <div style={{ maxHeight:550, overflow:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ borderBottom:`2px solid ${theme.divider}`, position:"sticky", top:0, background:theme.white, zIndex:1 }}>
            <th style={{ padding:"6px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>#</th>
            <SortTh sortKey="entry_time" sort={sort} setSort={setSort}>Entry</SortTh>
            <SortTh sortKey="exit_time" sort={sort} setSort={setSort}>Exit</SortTh>
            <th style={{ padding:"6px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>Dir</th>
            <SortTh sortKey="entry_price" sort={sort} setSort={setSort}>Entry $</SortTh>
            <SortTh sortKey="exit_price" sort={sort} setSort={setSort}>Exit $</SortTh>
            <th style={{ padding:"6px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>Qty</th>
            <SortTh sortKey="pnl" sort={sort} setSort={setSort}>P&L</SortTh>
            <th style={{ padding:"6px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>Reason</th>
            <th style={{ padding:"6px 10px", width:36 }}></th>
          </tr></thead>
          <tbody>{trades.map((t,i) => {
            const absIdx = page * PAGE + i;
            return (
              <tr key={absIdx} className="sl-row" style={{ borderBottom:`1px solid ${theme.divider}` }}>
                <td style={{ padding:"5px 10px", fontSize:10, color:theme.text4, fontFamily:fonts.mono }}>{absIdx+1}</td>
                <td style={{ padding:"5px 10px", fontSize:10, fontFamily:fonts.mono }}>{t.entry_time}</td>
                <td style={{ padding:"5px 10px", fontSize:10, fontFamily:fonts.mono }}>{t.exit_time}</td>
                <td style={{ padding:"5px 10px" }}><Pill bg={t.direction==="long"?theme.greenLight:theme.redLight} color={t.direction==="long"?theme.greenText:theme.redText}>{t.direction}</Pill></td>
                <td style={{ padding:"5px 10px", fontSize:10.5, fontFamily:fonts.mono }}>{Number(t.entry_price).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                <td style={{ padding:"5px 10px", fontSize:10.5, fontFamily:fonts.mono }}>{Number(t.exit_price).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                <td style={{ padding:"5px 10px", fontSize:10.5, fontFamily:fonts.mono }}>{t.qty}</td>
                <td style={{ padding:"5px 10px", fontSize:11, fontWeight:700, fontFamily:fonts.mono, color:t.pnl>=0?theme.greenText:theme.redText }}>{formatPnl(t.pnl)}</td>
                <td style={{ padding:"5px 10px", fontSize:10, fontFamily:fonts.mono, color:theme.text3 }}>{t.exit_reason}</td>
                <td style={{ padding:"5px 6px" }}>
                  <button
                    onClick={() => setVizState({ open: true, index: absIdx })}
                    title="Chart anzeigen"
                    style={{
                      width:26, height:26, borderRadius:6, border:`1px solid ${theme.border}`,
                      background:theme.tint, cursor:"pointer", fontSize:13,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      color:theme.text3, transition:"all 0.12s",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.background=theme.blueLight;e.currentTarget.style.color=theme.blueText;}}
                    onMouseLeave={e=>{e.currentTarget.style.background=theme.tint;e.currentTarget.style.color=theme.text3;}}
                  >
                    📈
                  </button>
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      {totalPages > 1 && <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:8, padding:"12px 0", borderTop:`1px solid ${theme.divider}` }}>
        <Button onClick={() => setPage(p => Math.max(0,p-1))} disabled={page===0}>Prev</Button>
        <span style={{ fontSize:11, color:theme.text3, fontFamily:fonts.mono }}>Page {page+1} of {totalPages}</span>
        <Button onClick={() => setPage(p => Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1}>Next</Button>
      </div>}

      {/* Trade Visualizer Modal */}
      {vizState.open && (
        <TradeVisualizer
          trade={sorted[vizState.index]}
          context={{
            instrument: data.instrument || "MGC",
            entry_tf:   data.timeframes?.entry   || "M5",
            context_tf: data.timeframes?.context  || "H1",
            regime_tf:  data.timeframes?.regime   || "H4",
          }}
          allTrades={sorted}
          currentIndex={vizState.index}
          onNavigate={idx => setVizState({ open: true, index: idx })}
          onClose={() => setVizState({ open: false, index: 0 })}
        />
      )}
    </Card>}
    {tab==="distribution" && <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
      {data.weekday_distribution && <Card><CardHeader title="By Weekday"/><div style={{ padding:"12px 16px" }}>
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d,i) => { const x = data.weekday_distribution[i]||{count:0,pnl:0}; const mp = Math.max(...Object.values(data.weekday_distribution).map(z=>Math.abs(z.pnl||0)))||1;
          return <div key={d} style={{ display:"flex", alignItems:"center", gap:8, padding:"3px 0" }}>
            <span style={{ width:26, fontSize:10, fontWeight:700, color:theme.text3, fontFamily:fonts.mono }}>{d}</span>
            <div style={{ flex:1, height:12, borderRadius:3, background:theme.tint, overflow:"hidden" }}><div style={{ width:`${Math.abs(x.pnl)/mp*100}%`, height:"100%", borderRadius:3, background:x.pnl>=0?theme.greenLight:theme.redLight }}/></div>
            <span style={{ width:56, textAlign:"right", fontSize:10, fontWeight:700, fontFamily:fonts.mono, color:x.pnl>=0?theme.greenText:theme.redText }}>{formatPnl(x.pnl)}</span>
            <span style={{ width:16, textAlign:"right", fontSize:9, color:theme.text4, fontFamily:fonts.mono }}>{x.count}</span>
          </div>;
        })}
      </div></Card>}
      {data.hour_distribution && <Card><CardHeader title="By Hour (UTC)"/><div style={{ padding:"12px 16px", maxHeight:340, overflow:"auto" }}>
        {Object.entries(data.hour_distribution).sort(([a],[b])=>Number(a)-Number(b)).filter(([,d])=>d.count>0).map(([hr,d]) => { const mp = Math.max(...Object.values(data.hour_distribution).map(z=>Math.abs(z.pnl||0)))||1;
          return <div key={hr} style={{ display:"flex", alignItems:"center", gap:8, padding:"3px 0" }}>
            <span style={{ width:26, fontSize:10, fontWeight:700, color:theme.text3, fontFamily:fonts.mono }}>{String(hr).padStart(2,"0")}h</span>
            <div style={{ flex:1, height:10, borderRadius:3, background:theme.tint, overflow:"hidden" }}><div style={{ width:`${Math.abs(d.pnl)/mp*100}%`, height:"100%", borderRadius:3, background:d.pnl>=0?theme.greenLight:theme.redLight }}/></div>
            <span style={{ width:56, textAlign:"right", fontSize:10, fontWeight:700, fontFamily:fonts.mono, color:d.pnl>=0?theme.greenText:theme.redText }}>{formatPnl(d.pnl)}</span>
            <span style={{ width:16, textAlign:"right", fontSize:9, color:theme.text4, fontFamily:fonts.mono }}>{d.count}</span>
          </div>;
        })}
      </div></Card>}
    </div>}
    {tab==="scenarios" && <ScenarioPlanner data={data}/>}

    {/* Sprint G · Monte-Carlo Tab — robustness analysis via trade-order permutation */}
    {tab==="monte_carlo" && data.monte_carlo && <>
      <Card style={{ padding:"14px 18px", marginBottom:16 }} className="sl-fadein">
        <CardHeader title="🎲 Monte-Carlo (Trade Order Permutation)" sub={`${data.monte_carlo.n_permutations} Permutationen über ${data.monte_carlo.n_trades} Trades`}/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, padding:"8px 0" }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Max DD Median</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono }}>${Math.round(data.monte_carlo.median_max_dd_usd||0)}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Max DD 95%-Quantil</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color:theme.redText }}>${Math.round(data.monte_carlo.q95_max_dd_usd||0)}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Max DD 99%-Quantil</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color:theme.redText }}>${Math.round(data.monte_carlo.q99_max_dd_usd||0)}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Final Equity Median</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono }}>${Math.round(data.monte_carlo.median_final_equity||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Final Equity 5%-Quantil</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono, color:theme.redText }}>${Math.round(data.monte_carlo.q05_final_equity||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Losing Streak 95%-Quantil</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono }}>{data.monte_carlo.q95_longest_losing_streak||"—"} Trades</div>
          </div>
        </div>
        <div style={{ fontSize:11, color:theme.text3, fontStyle:"italic", marginTop:6 }}>
          Bei anderer Trade-Reihenfolge: das 95%-Quantil zeigt den realistischen Worst-Case der Strategie.
        </div>
      </Card>

      {/* Deploy-Ready Pill — tri-state (YES / NO / INCONCLUSIVE) */}
      {data.deploy_ready && (() => {
        const dr = data.deploy_ready.deploy_ready;
        const isYes = dr === true;
        const isNo = dr === false;
        const isInconclusive = dr === null || dr === undefined || dr === "conditional";
        const border = isYes ? theme.green : isNo ? theme.red : theme.amber;
        const icon = isYes ? "✅" : isNo ? "❌" : "⚠️";
        const label = isYes ? "YES" : isNo ? "NO" : (dr === "conditional" ? "CONDITIONAL" : "INCONCLUSIVE");
        const textColor = isYes ? theme.greenText : isNo ? theme.redText : theme.amberText;
        const subText = isInconclusive
          ? "Keine Monte-Carlo-Constraints im YAML definiert. Ergänze goals.monte_carlo.deploy_constraints für Auto-Check."
          : "Strategie geprüft gegen goals.monte_carlo.deploy_constraints";
        return <Card style={{ padding:"14px 18px", marginBottom:16, borderLeft:`4px solid ${border}` }} className="sl-fadein">
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
            <span style={{ fontSize:22 }}>{icon}</span>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:textColor }}>Deploy-Ready: {label}</div>
              <div style={{ fontSize:11, color:theme.text3 }}>{subText}</div>
            </div>
          </div>
          {data.deploy_ready.checks && Object.keys(data.deploy_ready.checks).filter(k => k !== "_no_constraints").length > 0 && <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:8, fontSize:12, fontFamily:fonts.mono }}>
            {Object.entries(data.deploy_ready.checks).filter(([k]) => k !== "_no_constraints").map(([k,v]) => <div key={k} style={{
              padding:"8px 12px", borderRadius:6,
              background: v.status==="pass" ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
              border: `1px solid ${v.status==="pass" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
            }}>
              <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>{k}</div>
              <div style={{ fontSize:11, marginTop:3 }}>
                {v.status==="pass"?"✓":"✗"} actual: {typeof v.actual==="number"?v.actual.toFixed(2):v.actual} / limit: {typeof v.limit==="number"?v.limit.toFixed(2):v.limit}
              </div>
            </div>)}
          </div>}
        </Card>;
      })()}
    </>}

    {/* Fallback when MC tab selected but no MC data */}
    {tab==="monte_carlo" && !data.monte_carlo && <Card style={{ padding:32, textAlign:"center" }}>
      <div style={{ fontSize:13, color:theme.text3 }}>Monte-Carlo wurde für diesen Backtest nicht ausgeführt.</div>
      <div style={{ fontSize:11, color:theme.text4, marginTop:6 }}>Aktiviere goals.monte_carlo.enabled in der YAML, um MC automatisch laufen zu lassen.</div>
    </Card>}

    {/* Sprint H Phase 3 · Block-Bootstrap Tab — robustness analysis via block-resampling */}
    {tab==="synthetic_bootstrap" && data.synthetic_bootstrap && <>
      <Card style={{ padding:"14px 18px", marginBottom:16 }} className="sl-fadein">
        <CardHeader title="🔀 Block-Bootstrap (Trade-Resampling)" sub={`${data.synthetic_bootstrap.n_iter} synthetische Sequenzen · Block-Size ${data.synthetic_bootstrap.mean_block_size} · ${data.synthetic_bootstrap.n_trades_per_iter} Trades pro Sequenz`}/>
        
        {/* Robustness-Score Hero-Card */}
        <div style={{ display:"flex", alignItems:"center", gap:20, padding:"14px 0", borderBottom:`1px solid ${theme.borderLight||"#eee"}`, marginBottom:14 }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Robustness Score</div>
            <div style={{ fontSize:32, fontWeight:900, fontFamily:fonts.mono, color: data.synthetic_bootstrap.robustness_pct >= 70 ? theme.greenText : theme.redText }}>
              {(data.synthetic_bootstrap.robustness_pct||0).toFixed(1)}%
            </div>
          </div>
          <div style={{ fontSize:11, color:theme.text3, fontStyle:"italic", flex:1 }}>
            Anteil der synthetischen Sequenzen mit profitablem Endkapital. 
            {data.synthetic_bootstrap.robustness_pct >= 90 && " Exzellent — Strategy ist robust gegen Sequence-Variation."}
            {data.synthetic_bootstrap.robustness_pct >= 70 && data.synthetic_bootstrap.robustness_pct < 90 && " Gut — vereinzelte Sequenzen sind verlustreich, aber Mehrheit profitabel."}
            {data.synthetic_bootstrap.robustness_pct < 70 && " Schwach — Strategy stark sequence-abhängig, hohes Tail-Risk."}
          </div>
        </div>
        
        {/* Quantile-Karten */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, padding:"8px 0" }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Final PnL 5% (Worst)</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color: (data.synthetic_bootstrap.final_pnl_usd_5_pct||0)>=0?theme.greenText:theme.redText }}>${Math.round(data.synthetic_bootstrap.final_pnl_usd_5_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Final PnL Median</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono }}>${Math.round(data.synthetic_bootstrap.final_pnl_usd_50_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Final PnL 95% (Best)</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color:theme.greenText }}>${Math.round(data.synthetic_bootstrap.final_pnl_usd_95_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Max DD Median</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono }}>${Math.round(data.synthetic_bootstrap.max_dd_usd_50_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Max DD 95% (Worst)</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono, color:theme.redText }}>${Math.round(data.synthetic_bootstrap.max_dd_usd_95_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>Sharpe Median</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono }}>{(data.synthetic_bootstrap.sharpe_50_pct||0).toFixed(2)}</div>
          </div>
        </div>
        
        <div style={{ fontSize:11, color:theme.text3, fontStyle:"italic", marginTop:10 }}>
          Mit Block-Bootstrap (Politis-Romano Stationary, Block-Size {data.synthetic_bootstrap.mean_block_size}) werden Trades MIT Replacement gesampled — anders als Monte-Carlo testet das gegen ALTERNATIVE Sequenzen, nicht nur reordering.
          {data.synthetic_bootstrap.normalization_mode==="per_contract" && " Quantile sind per-Kontrakt normalisiert (signal-quality view)."}
        </div>
      </Card>

      {/* Fan-Chart: Equity-Curve mit Quantil-Bändern */}
      {data.synthetic_bootstrap.equity_curves && (() => {
        const ec = data.synthetic_bootstrap.equity_curves;
        if (!ec.p50 || ec.p50.length === 0) return null;
        
        const indices = ec.indices || ec.p50.map((_, i) => i);
        const allValues = [...ec.p05, ...ec.p25, ...ec.p50, ...ec.p75, ...ec.p95];
        const minY = Math.min(...allValues);
        const maxY = Math.max(...allValues);
        const yRange = maxY - minY || 1;
        const padY = yRange * 0.05;
        
        const W = 800, H = 320;
        const padLeft = 70, padRight = 20, padTop = 20, padBottom = 30;
        const plotW = W - padLeft - padRight;
        const plotH = H - padTop - padBottom;
        
        const xScale = (i) => padLeft + (i / Math.max(indices.length-1, 1)) * plotW;
        const yScale = (v) => padTop + plotH - ((v - (minY-padY)) / (yRange + 2*padY)) * plotH;
        
        // Build path for p50 line
        const p50Path = ec.p50.map((v, i) => `${i===0?"M":"L"} ${xScale(i).toFixed(1)} ${yScale(v).toFixed(1)}`).join(" ");
        
        // Build polygons for 5-95% band and 25-75% band (filled areas)
        const buildArea = (lower, upper) => {
          const top = upper.map((v, i) => `${xScale(i).toFixed(1)},${yScale(v).toFixed(1)}`).join(" ");
          const bot = lower.slice().reverse().map((v, i) => `${xScale(upper.length-1-i).toFixed(1)},${yScale(v).toFixed(1)}`).join(" ");
          return `${top} ${bot}`;
        };
        const band90 = buildArea(ec.p05, ec.p95);
        const band50 = buildArea(ec.p25, ec.p75);
        
        // Y-axis ticks (5 ticks)
        const yTicks = Array.from({length:5}, (_, i) => minY + (yRange * i / 4));
        // X-axis ticks (5 ticks)
        const xTicks = Array.from({length:5}, (_, i) => Math.round((indices.length-1) * i / 4));
        
        return <Card style={{ padding:"14px 18px", marginBottom:16 }} className="sl-fadein">
          <CardHeader title="Equity-Curve Fan-Chart" sub="5%/25%/50%/75%/95% Quantil-Bänder über alle synthetischen Sequenzen"/>
          <div style={{ overflowX:"auto", marginTop:10 }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", maxWidth:900, height:"auto" }}>
              {/* 90% band (5-95) light */}
              <polygon points={band90} fill="rgba(37,99,235,0.10)" stroke="none"/>
              {/* 50% band (25-75) medium */}
              <polygon points={band50} fill="rgba(37,99,235,0.22)" stroke="none"/>
              {/* Median line (p50) */}
              <path d={p50Path} stroke={theme.accent||"#2563EB"} strokeWidth="2" fill="none"/>
              
              {/* Y-axis ticks + labels */}
              {yTicks.map((v, i) => (
                <g key={`y${i}`}>
                  <line x1={padLeft} y1={yScale(v)} x2={padLeft-5} y2={yScale(v)} stroke={theme.text3||"#888"} strokeWidth="1"/>
                  <text x={padLeft-8} y={yScale(v)+4} textAnchor="end" fontSize="10" fontFamily={fonts.mono} fill={theme.text3||"#888"}>
                    ${Math.round(v).toLocaleString()}
                  </text>
                </g>
              ))}
              
              {/* X-axis ticks + labels */}
              {xTicks.map((tx, i) => (
                <g key={`x${i}`}>
                  <line x1={xScale(tx)} y1={padTop+plotH} x2={xScale(tx)} y2={padTop+plotH+5} stroke={theme.text3||"#888"} strokeWidth="1"/>
                  <text x={xScale(tx)} y={padTop+plotH+18} textAnchor="middle" fontSize="10" fontFamily={fonts.mono} fill={theme.text3||"#888"}>
                    {tx}
                  </text>
                </g>
              ))}
              
              {/* X-axis label */}
              <text x={padLeft + plotW/2} y={H-4} textAnchor="middle" fontSize="10" fill={theme.text4||"#aaa"}>Trade-Nummer in synthetischer Sequenz</text>
            </svg>
          </div>
          <div style={{ display:"flex", gap:14, justifyContent:"center", marginTop:6, fontSize:10, color:theme.text3 }}>
            <span><span style={{ display:"inline-block", width:10, height:10, background:"rgba(37,99,235,0.10)", marginRight:4 }}></span>5–95% (90% Confidence)</span>
            <span><span style={{ display:"inline-block", width:10, height:10, background:"rgba(37,99,235,0.22)", marginRight:4 }}></span>25–75% (Interquartile)</span>
            <span><span style={{ display:"inline-block", width:10, height:2, background:theme.accent||"#2563EB", marginRight:4, verticalAlign:"middle" }}></span>Median (p50)</span>
          </div>
        </Card>;
      })()}

      {/* Deploy-Ready Pill für Bootstrap (analog zu MC) */}
      {data.bootstrap_deploy_ready && (() => {
        const dr = data.bootstrap_deploy_ready.deploy_ready;
        const violations = data.bootstrap_deploy_ready.violations || [];
        const isYes = dr === true;
        const isNo = dr === false;
        const border = isYes ? theme.green : theme.red;
        const icon = isYes ? "✅" : "❌";
        const label = isYes ? "YES" : "NO";
        const textColor = isYes ? theme.greenText : theme.redText;
        return <Card style={{ padding:"14px 18px", marginBottom:16, borderLeft:`4px solid ${border}` }} className="sl-fadein">
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
            <span style={{ fontSize:22 }}>{icon}</span>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:textColor }}>Bootstrap Deploy-Ready: {label}</div>
              <div style={{ fontSize:11, color:theme.text3 }}>Strategie geprüft gegen goals.synthetic_bootstrap.deploy_constraints</div>
            </div>
          </div>
          {violations.length > 0 && <div style={{ marginTop:8, fontSize:11, color:theme.redText }}>
            {violations.map((v, i) => <div key={i}>✗ {v}</div>)}
          </div>}
        </Card>;
      })()}
    </>}

    {/* Fallback when Bootstrap tab selected but no Bootstrap data */}
    {tab==="synthetic_bootstrap" && !data.synthetic_bootstrap && <Card style={{ padding:32, textAlign:"center" }}>
      <div style={{ fontSize:13, color:theme.text3 }}>Block-Bootstrap wurde für diesen Backtest nicht ausgeführt.</div>
      <div style={{ fontSize:11, color:theme.text4, marginTop:6 }}>Aktiviere goals.synthetic_bootstrap.enabled in der YAML, um Bootstrap automatisch laufen zu lassen.</div>
    </Card>}

    {/* Sprint H Ebene 3: Bar-Level Block-Bootstrap Tab */}
    {tab==="bar_bootstrap" && data.bar_bootstrap && <>
      <Card>
        <CardHeader title="📊 Bar-Level Block-Bootstrap (Synthetic OHLC)" sub={`${data.bar_bootstrap.n_iter} synthetische Märkte · Block-Size ${data.bar_bootstrap.block_size} Bars · ${(data.bar_bootstrap.n_bars_per_iter||0).toLocaleString()} M5-Bars pro Markt`}/>

        <div style={{ padding:"16px 24px", display:"flex", alignItems:"center", gap:24 }}>
          <div>
            <div style={{ fontSize:11, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>Robustness</div>
            <div style={{ fontSize:32, fontWeight:900, fontFamily:fonts.mono, color: data.bar_bootstrap.robustness_pct >= 80 ? theme.greenText : data.bar_bootstrap.robustness_pct >= 60 ? "#E8A838" : theme.redText }}>
              {(data.bar_bootstrap.robustness_pct||0).toFixed(1)}%
            </div>
            <div style={{ fontSize:10, color:theme.text4 }}>% profitabler synthetischer Märkte</div>
          </div>
          <div style={{ fontSize:12, color:theme.text2, lineHeight:1.5, flex:1 }}>
            <b>Was das misst:</b> Strategy läuft auf {data.bar_bootstrap.n_iter} alternativen Marktrealitäten (Return-Based Block-Bootstrap auf OHLC-Bars).
            {data.bar_bootstrap.robustness_pct >= 90 && " Exzellent — Strategy ist robust gegen Markt-Variation."}
            {data.bar_bootstrap.robustness_pct >= 70 && data.bar_bootstrap.robustness_pct < 90 && " Gut — die meisten alternativen Märkte sind profitabel."}
            {data.bar_bootstrap.robustness_pct < 70 && " Schwach — Strategy stark abhängig von der historischen Markt-Sequenz."}
          </div>
        </div>

        <div style={{ padding:"0 24px 20px", display:"grid", gridTemplateColumns:"repeat(6, 1fr)", gap:12 }}>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>5% Worst</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color: (data.bar_bootstrap.final_pnl_usd_5_pct||0)>=0?theme.greenText:theme.redText }}>${Math.round(data.bar_bootstrap.final_pnl_usd_5_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>25%</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono }}>${Math.round(data.bar_bootstrap.final_pnl_usd_25_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>Median</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono }}>${Math.round(data.bar_bootstrap.final_pnl_usd_50_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>75%</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono }}>${Math.round(data.bar_bootstrap.final_pnl_usd_75_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>95% Best</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color:theme.greenText }}>${Math.round(data.bar_bootstrap.final_pnl_usd_95_pct||0).toLocaleString()}</div>
          </div>
          <div style={{ borderLeft:`2px solid ${theme.border}`, paddingLeft:12 }}>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>Original</div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:fonts.mono, color: (data.bar_bootstrap.original_pnl||0)>=0?theme.greenText:theme.redText }}>${Math.round(data.bar_bootstrap.original_pnl||0).toLocaleString()}</div>
          </div>
        </div>

        <div style={{ padding:"0 24px 20px", display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:12, borderTop:`1px solid ${theme.border}`, paddingTop:16 }}>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>Median DD</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono }}>${Math.round(data.bar_bootstrap.max_dd_usd_50_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>95% DD (worst case)</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono, color:theme.redText }}>${Math.round(data.bar_bootstrap.max_dd_usd_95_pct||0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:0.5 }}>Median Sharpe</div>
            <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono }}>{(data.bar_bootstrap.sharpe_50_pct||0).toFixed(2)}</div>
          </div>
        </div>

        <div style={{ padding:"0 24px 20px", fontSize:11, color:theme.text3, lineHeight:1.5 }}>
          <b>Methodik:</b> Return-Based Block-Bootstrap (V5+Tick-Snap) auf den M5-OHLC-Bars. Im Gegensatz zu Trade-Bootstrap (Ebene 2) re-runt die KOMPLETTE Strategy auf jedem synthetischen Markt — testet die Strategy-Logik in alternativen Marktrealitäten, nicht nur die Trade-Sequence.
        </div>
      </Card>

      {(() => {
        const q05 = data.bar_bootstrap.final_pnl_usd_5_pct || 0;
        const q25 = data.bar_bootstrap.final_pnl_usd_25_pct || 0;
        const q50 = data.bar_bootstrap.final_pnl_usd_50_pct || 0;
        const q75 = data.bar_bootstrap.final_pnl_usd_75_pct || 0;
        const q95 = data.bar_bootstrap.final_pnl_usd_95_pct || 0;
        const orig = data.bar_bootstrap.original_pnl || 0;
        const minV = Math.min(q05, orig, 0);
        const maxV = Math.max(q95, orig);
        const range = maxV - minV || 1;
        const W = 800, H = 200, PAD = 50;
        const innerW = W - 2*PAD;
        const xOf = (v) => PAD + ((v - minV) / range) * innerW;
        const zeroX = xOf(0);
        const origX = xOf(orig);
        let origPosition = "unknown";
        if (orig < q05) origPosition = "below q05 (worst-case)";
        else if (orig < q25) origPosition = "below q25 (bottom quartile)";
        else if (orig <= q75) origPosition = "in IQR (typical range)";
        else if (orig <= q95) origPosition = "above q75 (top quartile)";
        else origPosition = "above q95 (best-case outlier)";
        return (
          <Card style={{ marginTop:16 }}>
            <CardHeader title="📊 PnL-Verteilung über synthetische Märkte" sub={`Original-Backtest liegt ${origPosition} der Bootstrap-Verteilung`}/>
            <div style={{ padding:"16px 24px" }}>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
                <line x1={xOf(q05)} y1={H/2} x2={xOf(q95)} y2={H/2} stroke={theme.text3} strokeWidth="2"/>
                <line x1={xOf(q05)} y1={H/2 - 15} x2={xOf(q05)} y2={H/2 + 15} stroke={theme.text3} strokeWidth="2"/>
                <line x1={xOf(q95)} y1={H/2 - 15} x2={xOf(q95)} y2={H/2 + 15} stroke={theme.text3} strokeWidth="2"/>
                <rect x={xOf(q25)} y={H/2 - 25} width={xOf(q75) - xOf(q25)} height="50" fill={theme.greenText} fillOpacity="0.25" stroke={theme.greenText} strokeWidth="1.5"/>
                <line x1={xOf(q50)} y1={H/2 - 25} x2={xOf(q50)} y2={H/2 + 25} stroke={theme.greenText} strokeWidth="3"/>
                {minV < 0 && <line x1={zeroX} y1={H/2 - 40} x2={zeroX} y2={H/2 + 40} stroke={theme.text4} strokeWidth="1" strokeDasharray="3,3"/>}
                {minV < 0 && <text x={zeroX} y={H/2 + 55} textAnchor="middle" fontSize="9" fill={theme.text4} fontFamily={fonts.mono}>$0</text>}
                <line x1={origX} y1={H/2 - 35} x2={origX} y2={H/2 + 35} stroke="#E8A838" strokeWidth="2.5"/>
                <circle cx={origX} cy={H/2 - 35} r="6" fill="#E8A838" stroke="white" strokeWidth="1.5"/>
                <text x={origX} y={H/2 - 45} textAnchor="middle" fontSize="10" fontWeight="700" fill="#E8A838" fontFamily={fonts.mono}>Original ${Math.round(orig).toLocaleString()}</text>
                <text x={xOf(q05)} y={H/2 + 35} textAnchor="middle" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>q05</text>
                <text x={xOf(q05)} y={H/2 + 48} textAnchor="middle" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>${Math.round(q05/1000)}k</text>
                <text x={xOf(q50)} y={H/2 + 48} textAnchor="middle" fontSize="9" fontWeight="700" fill={theme.greenText} fontFamily={fonts.mono}>median ${Math.round(q50/1000)}k</text>
                <text x={xOf(q95)} y={H/2 + 35} textAnchor="middle" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>q95</text>
                <text x={xOf(q95)} y={H/2 + 48} textAnchor="middle" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>${Math.round(q95/1000)}k</text>
              </svg>
              <div style={{ marginTop:12, padding:"10px 14px", background: orig >= q25 && orig <= q75 ? `${theme.greenText}15` : `#E8A83815`, borderRadius:6, fontSize:11, color:theme.text2, lineHeight:1.5 }}>
                <b>Interpretation:</b> Die Box zeigt das Interquartile-Range (q25-q75) der {data.bar_bootstrap.n_iter} synthetischen Märkte. Das Original (orange) liegt {orig >= q25 && orig <= q75 ? "innerhalb dieser typischen Bandbreite — die historische Performance ist repräsentativ" : orig > q95 ? "OBERHALB von q95 — die historische Performance ist außergewöhnlich gut, wahrscheinlich profitiert die Strategy von der spezifischen historischen Sequenz (Selection-Bias-Verdacht)" : orig < q05 ? "UNTERHALB von q05 — sehr ungewöhnlich, die historische Performance ist deutlich schlechter als die meisten alternativen Märkte" : orig > q75 ? "im oberen Quartil (q75-q95) — historische Performance war gut, aber im Bereich was die Strategy realisieren kann" : "im unteren Quartil (q05-q25) — historische Performance war unterdurchschnittlich"}.
              </div>
            </div>
          </Card>
        );
      })()}

      {data.bar_bootstrap_deploy_ready && (
        <Card style={{ marginTop:16, padding:"16px 24px", display:"flex", alignItems:"center", gap:16, background: data.bar_bootstrap_deploy_ready.deploy_ready ? `${theme.greenText}15` : `${theme.redText}15`, borderLeft:`4px solid ${data.bar_bootstrap_deploy_ready.deploy_ready ? theme.greenText : theme.redText}` }}>
          <div style={{ fontSize:24 }}>{data.bar_bootstrap_deploy_ready.deploy_ready ? "✓" : "⚠"}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color: data.bar_bootstrap_deploy_ready.deploy_ready ? theme.greenText : theme.redText }}>
              {data.bar_bootstrap_deploy_ready.deploy_ready ? "Bar-Bootstrap Deploy-Ready" : "Bar-Bootstrap Deploy-Not-Ready"}
            </div>
            <div style={{ fontSize:11, color:theme.text3 }}>Strategie geprüft gegen goals.bar_bootstrap.deploy_constraints</div>
            {data.bar_bootstrap_deploy_ready.violations && data.bar_bootstrap_deploy_ready.violations.length > 0 && (
              <ul style={{ marginTop:8, marginBottom:0, paddingLeft:20, fontSize:11, color:theme.redText }}>
                {data.bar_bootstrap_deploy_ready.violations.map((v,i) => <li key={i}>{v}</li>)}
              </ul>
            )}
          </div>
        </Card>
      )}

      {/* Sprint H Ebene 3 Stufe 1: Market Regime Classification */}
      {data.bar_bootstrap.drift_summary && (() => {
        const ds = data.bar_bootstrap.drift_summary || {};
        const vs = data.bar_bootstrap.vol_summary || {};
        const rp = data.bar_bootstrap.regime_performance || {};
        const origDrift = data.bar_bootstrap.original_drift_pct || 0;
        const origVol = data.bar_bootstrap.original_realized_vol_pct || 0;
        const total = (ds["Strong Bull"]||0) + (ds["Bull"]||0) + (ds["Neutral"]||0) + (ds["Bear"]||0) + (ds["Strong Bear"]||0);
        const dominantClass = Object.entries(ds).reduce((a,b)=> (b[1]>a[1]?b:a), ["",0]);
        const isDegenerate = total > 0 && (dominantClass[1] / total) > 0.8;
        const driftRow = (label, color, n) => {
          const pct = total > 0 ? (n/total*100) : 0;
          return (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:130, fontSize:11, color:theme.text2 }}>{label}</div>
              <div style={{ flex:1, height:18, background:theme.tint, borderRadius:3, overflow:"hidden" }}>
                <div style={{ width:`${pct}%`, height:"100%", background:color }}/>
              </div>
              <div style={{ width:40, fontSize:11, fontFamily:fonts.mono, color:theme.text2, textAlign:"right" }}>{n}</div>
            </div>
          );
        };
        const volRow = (label, color, n) => {
          const pct = total > 0 ? (n/total*100) : 0;
          return (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:130, fontSize:11, color:theme.text2 }}>{label}</div>
              <div style={{ flex:1, height:18, background:theme.tint, borderRadius:3, overflow:"hidden" }}>
                <div style={{ width:`${pct}%`, height:"100%", background:color }}/>
              </div>
              <div style={{ width:40, fontSize:11, fontFamily:fonts.mono, color:theme.text2, textAlign:"right" }}>{n}</div>
            </div>
          );
        };
        return (
          <Card style={{ marginTop:16 }}>
            <CardHeader title="🌊 Markt-Klassifikation der synthetischen Märkte" sub={`Original: Drift ${origDrift>=0?"+":""}${origDrift.toFixed(1)}% · Vol ${origVol.toFixed(1)}% annualisiert`}/>
            <div style={{ padding:"16px 24px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:32 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:theme.text2, marginBottom:10, textTransform:"uppercase", letterSpacing:0.5 }}>Drift-Klasse</div>
                {driftRow("Strong Bull (>+60%)", theme.greenText, ds["Strong Bull"]||0)}
                {driftRow("Bull (+20 bis +60%)", "#7CC97C", ds["Bull"]||0)}
                {driftRow("Neutral (±20%)", theme.text3, ds["Neutral"]||0)}
                {driftRow("Bear (-20 bis -60%)", "#E8A838", ds["Bear"]||0)}
                {driftRow("Strong Bear (<-60%)", theme.redText, ds["Strong Bear"]||0)}
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:theme.text2, marginBottom:10, textTransform:"uppercase", letterSpacing:0.5 }}>Volatility-Regime</div>
                {volRow("Quiet (<70% Orig)", "#9BC4E2", vs["Quiet"]||0)}
                {volRow("Normal (70-130%)", theme.text3, vs["Normal"]||0)}
                {volRow("Choppy (>130%)", "#E8A838", vs["Choppy"]||0)}
              </div>
            </div>

            {/* Auto-Warnung wenn Verteilung degeneriert ist */}
            {isDegenerate && (
              <div style={{ margin:"0 24px 16px", padding:"12px 14px", background:`#E8A83815`, borderLeft:`4px solid #E8A838`, borderRadius:6 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#B07020", marginBottom:4 }}>
                  ⚠ Klassifikations-Bias erkannt: {Math.round(dominantClass[1]/total*100)}% der Märkte sind "{dominantClass[0]}"
                </div>
                <div style={{ fontSize:11, color:theme.text2, lineHeight:1.5 }}>
                  Return-Based Block-Bootstrap kann den Mean-Drift der Original-Returns nicht umkehren. Da der Original-Markt einen Drift von {origDrift>=0?"+":""}{origDrift.toFixed(0)}% hat, produzieren auch alle synthetischen Märkte ähnliche Drifts. <b>Die "Robustness {(data.bar_bootstrap.robustness_pct||0).toFixed(0)}%" gilt nur für Variationen innerhalb dieses Regimes</b>, nicht für andere Marktphasen. Für echte Multi-Regime-Validierung wird ein externes Datenset einer anderen Marktphase benötigt (Sprint H Ebene 3.5 geplant: Gold-Bear-Bootstrap 2011-2015).
                </div>
              </div>
            )}

            {/* Strategy-Performance pro Drift-Klasse */}
            <div style={{ padding:"0 24px 20px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:theme.text2, marginBottom:10, textTransform:"uppercase", letterSpacing:0.5 }}>Strategy-Performance pro Drift-Klasse</div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:fonts.mono }}>
                <thead>
                  <tr style={{ borderBottom:`2px solid ${theme.border}`, color:theme.text3, textAlign:"left" }}>
                    <th style={{ padding:"6px 8px" }}>Klasse</th>
                    <th style={{ padding:"6px 8px", textAlign:"right" }}>n</th>
                    <th style={{ padding:"6px 8px", textAlign:"right" }}>Median PnL</th>
                    <th style={{ padding:"6px 8px", textAlign:"right" }}>Robustness</th>
                    <th style={{ padding:"6px 8px", textAlign:"right" }}>Median DD</th>
                  </tr>
                </thead>
                <tbody>
                  {["Strong Bull","Bull","Neutral","Bear","Strong Bear"].map(cls => {
                    const r = rp[cls] || {n:0};
                    const empty = !r || r.n === 0;
                    return (
                      <tr key={cls} style={{ borderBottom:`1px solid ${theme.divider}`, color: empty ? theme.text4 : theme.text2 }}>
                        <td style={{ padding:"6px 8px" }}>{cls}</td>
                        <td style={{ padding:"6px 8px", textAlign:"right" }}>{r.n||0}</td>
                        <td style={{ padding:"6px 8px", textAlign:"right", color: empty ? theme.text4 : ((r.median_pnl||0)>=0?theme.greenText:theme.redText), fontWeight:700 }}>{empty?"—":"$"+Math.round(r.median_pnl||0).toLocaleString()}</td>
                        <td style={{ padding:"6px 8px", textAlign:"right", fontWeight:700 }}>{empty?"—":(r.robustness_pct||0).toFixed(0)+"%"}</td>
                        <td style={{ padding:"6px 8px", textAlign:"right" }}>{empty?"—":"$"+Math.round(r.median_dd_usd||0).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Scatter: Market Drift vs Strategy PnL */}
            {data.bar_bootstrap.per_iter_stats && data.bar_bootstrap.per_iter_stats.length > 0 && (() => {
              const stats = data.bar_bootstrap.per_iter_stats;
              const drifts = stats.map(s => s.market_drift_pct);
              const pnls = stats.map(s => s.net_pnl);
              const minD = Math.min(...drifts, 0);
              const maxD = Math.max(...drifts);
              const minP = Math.min(...pnls, 0);
              const maxP = Math.max(...pnls);
              const SW = 800, SH = 240, PAD = 50;
              const xOf = d => PAD + ((d - minD) / (maxD - minD || 1)) * (SW - 2*PAD);
              const yOf = p => SH - PAD - ((p - minP) / (maxP - minP || 1)) * (SH - 2*PAD);
              const classColor = (cls) => {
                if (cls === "Strong Bull") return theme.greenText;
                if (cls === "Bull") return "#7CC97C";
                if (cls === "Neutral") return theme.text3;
                if (cls === "Bear") return "#E8A838";
                if (cls === "Strong Bear") return theme.redText;
                return theme.text3;
              };
              return (
                <div style={{ padding:"0 24px 20px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:theme.text2, marginBottom:10, textTransform:"uppercase", letterSpacing:0.5 }}>Markt-Drift vs Strategy-PnL (Scatter)</div>
                  <svg viewBox={`0 0 ${SW} ${SH}`} style={{ width:"100%", height:"auto" }}>
                    {/* axes */}
                    <line x1={PAD} y1={SH-PAD} x2={SW-PAD} y2={SH-PAD} stroke={theme.text4} strokeWidth="1"/>
                    <line x1={PAD} y1={PAD} x2={PAD} y2={SH-PAD} stroke={theme.text4} strokeWidth="1"/>
                    {/* zero lines if visible */}
                    {minP < 0 && maxP > 0 && (
                      <line x1={PAD} y1={yOf(0)} x2={SW-PAD} y2={yOf(0)} stroke={theme.text4} strokeWidth="1" strokeDasharray="3,3"/>
                    )}
                    {minD < 0 && maxD > 0 && (
                      <line x1={xOf(0)} y1={PAD} x2={xOf(0)} y2={SH-PAD} stroke={theme.text4} strokeWidth="1" strokeDasharray="3,3"/>
                    )}
                    {/* original as orange star reference */}
                    {(() => {
                      const oX = xOf(origDrift);
                      const oY = yOf(data.bar_bootstrap.original_pnl||0);
                      if (oX >= PAD && oX <= SW-PAD && oY >= PAD && oY <= SH-PAD) {
                        return (
                          <>
                            <circle cx={oX} cy={oY} r="8" fill="#E8A838" stroke="white" strokeWidth="2"/>
                            <text x={oX+12} y={oY-8} fontSize="10" fontWeight="700" fill="#E8A838" fontFamily={fonts.mono}>Original</text>
                          </>
                        );
                      }
                      return null;
                    })()}
                    {/* scatter points */}
                    {stats.map((s, i) => (
                      <circle key={i} cx={xOf(s.market_drift_pct)} cy={yOf(s.net_pnl)} r="5" fill={classColor(s.drift_class)} fillOpacity="0.7" stroke={classColor(s.drift_class)} strokeWidth="1.5"/>
                    ))}
                    {/* axis labels */}
                    <text x={SW/2} y={SH-10} textAnchor="middle" fontSize="10" fill={theme.text3} fontFamily={fonts.mono}>Markt-Drift %</text>
                    <text x={15} y={SH/2} textAnchor="middle" fontSize="10" fill={theme.text3} fontFamily={fonts.mono} transform={`rotate(-90 15 ${SH/2})`}>Strategy-PnL $</text>
                    {/* axis ticks */}
                    <text x={PAD} y={SH-PAD+15} textAnchor="middle" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>{minD.toFixed(0)}%</text>
                    <text x={SW-PAD} y={SH-PAD+15} textAnchor="middle" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>{maxD.toFixed(0)}%</text>
                    <text x={PAD-8} y={SH-PAD+3} textAnchor="end" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>${Math.round(minP/1000)}k</text>
                    <text x={PAD-8} y={PAD+3} textAnchor="end" fontSize="9" fill={theme.text3} fontFamily={fonts.mono}>${Math.round(maxP/1000)}k</text>
                  </svg>
                  <div style={{ fontSize:10, color:theme.text3, marginTop:4, lineHeight:1.4 }}>
                    Jeder Punkt ist ein synthetic Markt. Farbe = Drift-Klasse. Steile positive Korrelation = Strategy-PnL hängt stark vom Markt-Drift ab (Long-Bias). Flache Verteilung = Strategy ist regime-unabhängig.
                  </div>
                </div>
              );
            })()}
          </Card>
        );
      })()}
    </>}

    {/* Fallback when Bar-Bootstrap tab selected but no Bar-Bootstrap data */}
    {tab==="bar_bootstrap" && !data.bar_bootstrap && <Card style={{ padding:32, textAlign:"center" }}>
      <div style={{ fontSize:13, color:theme.text3 }}>Bar-Level Block-Bootstrap wurde für diesen Backtest nicht ausgeführt.</div>
      <div style={{ fontSize:11, color:theme.text4, marginTop:6 }}>Aktiviere goals.bar_bootstrap.enabled in der YAML oder setze request_body.bar_bootstrap_enabled=true.</div>
    </Card>}
  </div>;
}

/* ═══ SCENARIO PLANNER ═══════════════════════════════ */
/**
 * Post-hoc What-If Analyzer for Backtest results.
 * Recomputes key metrics based on adjusted Account Size, Risk %, Commission, Min-RR filter.
 * Uses proportional per-trade PnL scaling — assumes original strategy ran with fixed fractional risk sizing.
 */
function ScenarioPlanner({ data }) {
  const trades = data?.trades || [];

  // Estimate original risk-dollar ("1R") from average loser
  const losers = trades.filter(t => (t.pnl || 0) < 0);
  const avgLossUsd = losers.length > 0 ? Math.abs(losers.reduce((s, t) => s + t.pnl, 0) / losers.length) : 100;

  // Derive baseline account+risk from YAML-hinted values or sensible defaults
  const baseAccount = data?.account_size || data?.initial_capital || 50000;
  const basePct = data?.risk_pct || 0.4;  // % (not decimal)
  const baseCommission = data?.commission || 0;  // per-trade

  // Sliders state
  const [accountSize, setAccountSize] = useState(baseAccount);
  const [riskPct, setRiskPct] = useState(basePct);
  const [commission, setCommission] = useState(baseCommission);
  const [minRR, setMinRR] = useState(0);

  const oldRiskDollar = baseAccount * (basePct / 100);
  const newRiskDollar = accountSize * (riskPct / 100);
  const pnlScale = oldRiskDollar > 0 ? newRiskDollar / oldRiskDollar : 1;
  const commissionDelta = commission - baseCommission;

  // Recompute trade PnL under scenario: scale pnl + commission adjustment + RR filter
  const scenarioTrades = useMemo(() => {
    return trades
      .map(t => {
        // Approximate RR of this trade from PnL/avgLoss
        const rr = avgLossUsd > 0 ? t.pnl / avgLossUsd : 0;
        return { ...t, _rr: rr };
      })
      .filter(t => t._rr === 0 || t._rr <= 0 || Math.abs(t._rr) >= minRR)  // Keep losers + winners >= minRR
      .map(t => ({
        ...t,
        newPnl: t.pnl * pnlScale - commissionDelta,
      }));
  }, [trades, pnlScale, commissionDelta, minRR, avgLossUsd]);

  // Recompute metrics
  const filtered = scenarioTrades;
  const totalPnl = filtered.reduce((s, t) => s + t.newPnl, 0);
  const winners = filtered.filter(t => t.newPnl > 0).length;
  const losersNew = filtered.filter(t => t.newPnl < 0).length;
  const total = filtered.length;
  const winRate = total > 0 ? (winners / total) * 100 : 0;
  const grossWin = filtered.filter(t => t.newPnl > 0).reduce((s, t) => s + t.newPnl, 0);
  const grossLoss = Math.abs(filtered.filter(t => t.newPnl < 0).reduce((s, t) => s + t.newPnl, 0));
  const pf = grossLoss > 0 ? grossWin / grossLoss : 0;
  const avgWin = winners > 0 ? grossWin / winners : 0;
  const avgLoss = losersNew > 0 ? grossLoss / losersNew : 0;
  const expectancy = total > 0 ? totalPnl / total : 0;

  // Equity curve + Max DD
  let peak = 0, maxDD = 0, equity = 0;
  for (const t of filtered) {
    equity += t.newPnl;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDD) maxDD = dd;
  }
  const maxDDPct = accountSize > 0 ? (maxDD / accountSize) * 100 : 0;

  // Risk-of-Ruin indicator (account relative)
  const ruinMarker = maxDD >= accountSize * 0.5 ? "💀" : maxDD >= accountSize * 0.25 ? "⚠️" : "✓";
  const ruinColor = maxDD >= accountSize * 0.5 ? theme.redText : maxDD >= accountSize * 0.25 ? theme.amberText : theme.greenText;

  // Baseline comparison (original result)
  const origPnl = data.net_pnl || 0;
  const pnlDelta = totalPnl - origPnl;
  const pnlDeltaPct = origPnl !== 0 ? (pnlDelta / Math.abs(origPnl)) * 100 : 0;

  const reset = () => {
    setAccountSize(baseAccount);
    setRiskPct(basePct);
    setCommission(baseCommission);
    setMinRR(0);
  };

  return <div className="sl-fadein">
    {/* Info header */}
    <Card style={{marginBottom:16,padding:"14px 18px",background:"rgba(37,99,235,0.03)",border:`1px solid rgba(37,99,235,0.15)`}}>
      <div style={{fontSize:13,fontWeight:800,color:theme.text,marginBottom:6}}>🎯 Scenario Planner — What-If-Analyse</div>
      <div style={{fontSize:12,color:theme.text2,lineHeight:1.6}}>
        Experimentiere mit <strong>Account Size</strong>, <strong>Risk %</strong>, <strong>Commission</strong> und <strong>Min-RR-Filter</strong>, um zu sehen wie sich das Ergebnis verändert hätte.
        Das rechnet in Echtzeit, ohne neuen Backtest — die Trades werden proportional zum Risiko-Dollarbetrag skaliert.
      </div>
      <div style={{fontSize:11,color:theme.text3,marginTop:6,fontStyle:"italic"}}>
        Baseline: {formatPnl(origPnl)} · Account ${baseAccount.toLocaleString()} · Risk {basePct}% · Commission ${baseCommission}/Trade · 1R ≈ ${avgLossUsd.toFixed(0)}
      </div>
    </Card>

    {/* Sliders */}
    <Card style={{marginBottom:16,padding:"18px 22px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:800,color:theme.text}}>⚙️ Parameter anpassen</div>
        <Button onClick={reset} style={{fontSize:11,padding:"5px 11px"}}>Reset</Button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:22}}>
        {/* Account Size */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
            <label style={{fontSize:11,fontWeight:700,color:theme.text2,textTransform:"uppercase",letterSpacing:"0.5px"}}>💰 Account Size</label>
            <span style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:theme.blueText}}>${accountSize.toLocaleString()}</span>
          </div>
          <input type="range" min={5000} max={500000} step={1000} value={accountSize} onChange={e=>setAccountSize(Number(e.target.value))} style={{width:"100%",accentColor:theme.blue}}/>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:theme.text4,fontFamily:fonts.mono,marginTop:3}}>
            <span>$5k</span><span>$500k</span>
          </div>
        </div>

        {/* Risk % */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
            <label style={{fontSize:11,fontWeight:700,color:theme.text2,textTransform:"uppercase",letterSpacing:"0.5px"}}>🎲 Risk pro Trade</label>
            <span style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:theme.blueText}}>{riskPct.toFixed(2)}%</span>
          </div>
          <input type="range" min={0.05} max={2.0} step={0.05} value={riskPct} onChange={e=>setRiskPct(Number(e.target.value))} style={{width:"100%",accentColor:theme.blue}}/>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:theme.text4,fontFamily:fonts.mono,marginTop:3}}>
            <span>0.05%</span><span>2.0%</span>
          </div>
          <div style={{fontSize:10,color:theme.text3,marginTop:4,fontFamily:fonts.mono}}>= ${newRiskDollar.toFixed(0)} pro Trade</div>
        </div>

        {/* Commission */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
            <label style={{fontSize:11,fontWeight:700,color:theme.text2,textTransform:"uppercase",letterSpacing:"0.5px"}}>💳 Commission/Trade</label>
            <span style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:theme.blueText}}>${commission.toFixed(2)}</span>
          </div>
          <input type="range" min={0} max={20} step={0.25} value={commission} onChange={e=>setCommission(Number(e.target.value))} style={{width:"100%",accentColor:theme.blue}}/>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:theme.text4,fontFamily:fonts.mono,marginTop:3}}>
            <span>$0</span><span>$20</span>
          </div>
          <div style={{fontSize:10,color:theme.text3,marginTop:4,fontFamily:fonts.mono}}>IBKR MGC ~$1-2 · Topstep ~$2-4</div>
        </div>

        {/* Min RR Filter */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
            <label style={{fontSize:11,fontWeight:700,color:theme.text2,textTransform:"uppercase",letterSpacing:"0.5px"}}>🎯 Min-RR der Winners</label>
            <span style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:theme.blueText}}>{minRR.toFixed(1)}R</span>
          </div>
          <input type="range" min={0} max={5} step={0.25} value={minRR} onChange={e=>setMinRR(Number(e.target.value))} style={{width:"100%",accentColor:theme.blue}}/>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:theme.text4,fontFamily:fonts.mono,marginTop:3}}>
            <span>alle nehmen</span><span>nur ≥5R</span>
          </div>
          <div style={{fontSize:10,color:theme.text3,marginTop:4,fontFamily:fonts.mono}}>Filtert Winners &lt; {minRR}R heraus</div>
        </div>
      </div>
    </Card>

    {/* Results */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10,marginBottom:14}}>
      <Card style={{padding:"14px 16px",border:`2px solid ${totalPnl>=0?theme.green:theme.red}`,background:totalPnl>=0?"rgba(22,163,74,0.02)":"rgba(239,68,68,0.02)"}}>
        <div style={{fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>Neuer Net P&L</div>
        <div style={{fontSize:22,fontWeight:900,fontFamily:fonts.mono,color:totalPnl>=0?theme.greenText:theme.redText,lineHeight:1}}>{formatPnl(totalPnl)}</div>
        <div style={{fontSize:10,color:pnlDelta>=0?theme.greenText:theme.redText,fontFamily:fonts.mono,marginTop:4}}>
          {pnlDelta>=0?"+":""}{formatPnl(pnlDelta)} ({pnlDeltaPct>=0?"+":""}{pnlDeltaPct.toFixed(1)}%)
        </div>
      </Card>
      <Card style={{padding:"14px 16px"}}>
        <div style={{fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>Max Drawdown</div>
        <div style={{fontSize:18,fontWeight:800,fontFamily:fonts.mono,color:theme.redText,lineHeight:1}}>${maxDD.toFixed(0)}</div>
        <div style={{fontSize:11,color:ruinColor,fontFamily:fonts.mono,fontWeight:700,marginTop:4}}>
          {ruinMarker} {maxDDPct.toFixed(1)}% vom Account
        </div>
      </Card>
      <Card style={{padding:"14px 16px"}}>
        <div style={{fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>Trades</div>
        <div style={{fontSize:18,fontWeight:800,fontFamily:fonts.mono,color:theme.text,lineHeight:1}}>{total}</div>
        <div style={{fontSize:10,color:theme.text3,fontFamily:fonts.mono,marginTop:4}}>{winners}W / {losersNew}L · {winRate.toFixed(1)}%</div>
      </Card>
      <Card style={{padding:"14px 16px"}}>
        <div style={{fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>Profit Factor</div>
        <div style={{fontSize:18,fontWeight:800,fontFamily:fonts.mono,color:pf>=1.3?theme.greenText:pf>=1?theme.text:theme.redText,lineHeight:1}}>{pf.toFixed(2)}</div>
        <div style={{fontSize:10,color:theme.text3,fontFamily:fonts.mono,marginTop:4}}>{pf>=1.5?"solide":pf>=1.2?"ok":pf>=1?"borderline":"⚠ negativ"}</div>
      </Card>
      <Card style={{padding:"14px 16px"}}>
        <div style={{fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>Expectancy/Trade</div>
        <div style={{fontSize:18,fontWeight:800,fontFamily:fonts.mono,color:expectancy>=0?theme.greenText:theme.redText,lineHeight:1}}>${expectancy.toFixed(0)}</div>
        <div style={{fontSize:10,color:theme.text3,fontFamily:fonts.mono,marginTop:4}}>Ø Win ${avgWin.toFixed(0)} · Ø Loss ${avgLoss.toFixed(0)}</div>
      </Card>
      <Card style={{padding:"14px 16px"}}>
        <div style={{fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>Return on Account</div>
        <div style={{fontSize:18,fontWeight:800,fontFamily:fonts.mono,color:totalPnl>=0?theme.greenText:theme.redText,lineHeight:1}}>{accountSize>0?((totalPnl/accountSize)*100).toFixed(1):0}%</div>
        <div style={{fontSize:10,color:theme.text3,fontFamily:fonts.mono,marginTop:4}}>vs {maxDDPct.toFixed(1)}% DD</div>
      </Card>
    </div>

    {/* Interpretation */}
    <Card style={{padding:"14px 18px",background:theme.tint}}>
      <div style={{fontSize:12,fontWeight:700,color:theme.text,marginBottom:8}}>💡 Interpretation</div>
      <div style={{fontSize:12,color:theme.text2,lineHeight:1.7}}>
        {maxDD >= accountSize * 0.5 && <div style={{color:theme.redText,fontWeight:600,marginBottom:4}}>💀 Kritisch: Max DD ≥ 50% vom Account — Risk-of-Ruin. Reduziere Risk% oder erhöhe Account.</div>}
        {maxDD >= accountSize * 0.25 && maxDD < accountSize * 0.5 && <div style={{color:theme.amberText,fontWeight:600,marginBottom:4}}>⚠️ Vorsicht: DD zwischen 25-50% vom Account. Psychologisch schwer durchzuhalten.</div>}
        {maxDD < accountSize * 0.25 && total > 0 && <div style={{color:theme.greenText,fontWeight:600,marginBottom:4}}>✓ DD unter 25% vom Account — psychologisch tragbar.</div>}
        {pnlDeltaPct > 10 && <div>Diese Konfiguration wäre <strong>{pnlDeltaPct.toFixed(0)}% besser</strong> als das Original gewesen.</div>}
        {pnlDeltaPct < -10 && <div>Diese Konfiguration wäre <strong>{Math.abs(pnlDeltaPct).toFixed(0)}% schlechter</strong> als das Original gewesen.</div>}
        {minRR > 0 && <div>Min-RR-Filter entfernt {trades.length - total} Trade(s) mit schwacher RR. {winRate > ((data.win_rate || 0.4) * 100) ? "Win-Rate gestiegen ✓" : "Win-Rate gesunken — Filter bringt nichts."}</div>}
        {commissionDelta !== 0 && <div>Commission-Änderung: {commissionDelta > 0 ? `+$${commissionDelta.toFixed(2)}` : `-$${Math.abs(commissionDelta).toFixed(2)}`}/Trade · Gesamt-Impact ≈ {formatPnl(-commissionDelta * total)}</div>}
      </div>
    </Card>
  </div>;
}

/* ═══ BT COMPARE ═════════════════════════════════ */
export function BtCompare({ ids, onClose }) {
  const [d1,setD1]=useState(null); const [d2,setD2]=useState(null); const [ld,setLd]=useState(true);
  useEffect(() => { if(!ids||ids.length<2) return; setLd(true); Promise.all([getApi(`/results/backtests/${ids[0]}`),getApi(`/results/backtests/${ids[1]}`)]).then(([a,b]) => { setD1(a); setD2(b); setLd(false); }); }, [ids]);
  if (ld) return <SkeletonCards n={2}/>;
  if (!d1||!d2) return <Card style={{padding:32,textAlign:"center"}}><div style={{color:theme.redText}}>Could not load both runs</div><Button onClick={onClose} style={{marginTop:12}}>Back</Button></Card>;
  const metrics = [["Net P&L","net_pnl",v=>formatPnl(v)],["Win Rate","win_rate",v=>`${(v*100).toFixed(1)}%`],["Profit Factor","profit_factor",v=>v?.toFixed(2)],["Sharpe","sharpe",v=>v?.toFixed(2)],["Max DD %","max_drawdown",v=>`${v?.toFixed(1)}%`],["Trades","total_trades",v=>v],["Expectancy","expectancy",v=>formatPnl(v)],["Avg RR","avg_rr",v=>v?.toFixed(2)],["Sortino","sortino",v=>v?.toFixed(2)]];
  const better = (a,b,key) => { if(key==="max_drawdown") return(a||0)<(b||0); return(a||0)>(b||0); };
  return <div className="sl-fadein">
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
      <BackBtn onClick={onClose}/>
      <div style={{ fontSize:18, fontWeight:900 }}>Compare #{ids[0]} vs #{ids[1]}</div>
    </div>
    <Card><table style={{ width:"100%", borderCollapse:"collapse" }}>
      <thead><tr style={{ borderBottom:`2px solid ${theme.divider}` }}>
        <th style={{ padding:"10px 16px", fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left", width:"30%" }}>Metric</th>
        <th style={{ padding:"10px 16px", fontSize:10, fontWeight:700, color:theme.text4, textAlign:"center", width:"35%" }}>#{ids[0]} {d1.instrument}</th>
        <th style={{ padding:"10px 16px", fontSize:10, fontWeight:700, color:theme.text4, textAlign:"center", width:"35%" }}>#{ids[1]} {d2.instrument}</th>
      </tr></thead>
      <tbody>{metrics.map(([label,key,fmt]) => { const v1=d1[key],v2=d2[key]; const b1=better(v1,v2,key), b2=better(v2,v1,key);
        return <tr key={key} className="sl-row" style={{ borderBottom:`1px solid ${theme.divider}` }}>
          <td style={{ padding:"8px 16px", fontSize:12, fontWeight:600, color:theme.text2 }}>{label}</td>
          <td style={{ padding:"8px 16px", textAlign:"center", fontSize:13, fontWeight:700, fontFamily:fonts.mono, color:b1&&!b2?theme.greenText:theme.text, background:b1&&!b2?"rgba(22,163,74,0.04)":"transparent" }}>{fmt(v1)}</td>
          <td style={{ padding:"8px 16px", textAlign:"center", fontSize:13, fontWeight:700, fontFamily:fonts.mono, color:b2&&!b1?theme.greenText:theme.text, background:b2&&!b1?"rgba(22,163,74,0.04)":"transparent" }}>{fmt(v2)}</td>
        </tr>;
      })}</tbody>
    </table></Card>
  </div>;
}

/* ═══ OPTIMIZER DETAIL ════════════════════════════ */

function ParamsTab({ trials }) {
  const [topN, setTopN] = useState(10);
  const validT2 = trials.filter(t => !t.is_disqualified);
  const top = [...validT2].sort((a,b) => (b.compositeScore||0)-(a.compositeScore||0)).slice(0,topN);
  // Hardcoded technical skips (echte Engine-Internals, keine Strategie-Params).
  const skipKeys = new Set(["instrument","start_date","end_date","strategy_file","early_stop_dd","bad_hours","partial_pcts","trail_mode"]);

  // Dynamic constant detection: Params die über ALLE Trials konstant sind werden im Backend
  // nicht optimiert (hardcoded oder aus YAML übernommen). Sie hier auch ausblenden um nur
  // tatsächlich variable Parameter zu zeigen.
  const allTrialKeys = [...new Set(trials.flatMap(t=>Object.keys(t.params||{})))];
  const lockedKeys = new Set();
  if (trials.length >= 2) {
    for (const k of allTrialKeys) {
      const firstVal = trials[0]?.params?.[k];
      const isConstant = trials.every(t => {
        const v = t.params?.[k];
        return v === firstVal || (v == null && firstVal == null);
      });
      if (isConstant) lockedKeys.add(k);
    }
  }

  const allKeys = allTrialKeys.filter(k => !skipKeys.has(k) && !lockedKeys.has(k));
  
  const paramStats = {};
  for (let ki=0; ki<allKeys.length; ki++) {
    const k = allKeys[ki];
    const vals = top.map(t=>(t.params||{})[k]).filter(v=>v!=null);
    const freq = {};
    for (let vi=0; vi<vals.length; vi++) { const s=String(vals[vi]); freq[s]=(freq[s]||0)+1; }
    const sorted = Object.entries(freq).sort((a,b)=>b[1]-a[1]);
    const topVal = sorted[0];
    const consensus = topVal ? topVal[1]/vals.length : 0;
    const numVals = vals.filter(v=>typeof v==="number");
    paramStats[k] = {
      consensusVal: topVal ? topVal[0] : undefined,
      consensus, allSame: consensus >= 0.8,
      min: numVals.length ? Math.min(...numVals) : null,
      max: numVals.length ? Math.max(...numVals) : null,
      topValues: sorted.slice(0,3)
    };
  }

  const groups = [
    { label:"Stop Loss", icon:"🛡️", color:"#dc2626", keys:["sl_atr_mult","atr_floor_mult"] },
    { label:"Take Profit", icon:"🎯", color:"#059669", keys:["tp1_rr","tp2_rr","tpl_rr","rr1","rr2"] },
    { label:"Break Even & Trail", icon:"⚖️", color:"#0891b2", keys:["be_trigger_rr","be_after_rr","post_be_trail_atr_mult","runner_trail_atr_mult"] },
    { label:"Structure", icon:"🔧", color:"#7c3aed", keys:["entry_swing_length","context_swing_length","max_pattern_age","context_choch_max_age","break_confirm","entry_mode"] },
    { label:"Toggles", icon:"🔘", color:"#6b7280", keys:["be_enabled","ema_enabled","fvg_enabled","regime_enabled"] },
  ];
  const groupedKeys = new Set(groups.flatMap(g=>g.keys));
  const otherKeys = allKeys.filter(k=>!groupedKeys.has(k));
  if (otherKeys.length) groups.push({label:"Weitere",icon:"📎",color:"#94a3b8",keys:otherKeys});
  const stableCount = allKeys.filter(k=>paramStats[k] && paramStats[k].allSame).length;

  return <>
    <div style={{display:"flex",gap:16,marginBottom:14,padding:"12px 16px",borderRadius:10,background:"rgba(37,99,235,0.03)",border:`1px solid ${theme.divider}`,alignItems:"center",flexWrap:"wrap"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:24,fontWeight:800,color:theme.greenText,fontFamily:fonts.mono}}>{stableCount}</div>
        <div style={{fontSize:11,color:theme.text3}}>stabil</div>
      </div>
      <div style={{width:1,height:40,background:theme.divider}}/>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:24,fontWeight:800,color:theme.amberText,fontFamily:fonts.mono}}>{allKeys.length-stableCount}</div>
        <div style={{fontSize:11,color:theme.text3}}>variabel</div>
      </div>
      <div style={{width:1,height:40,background:theme.divider}}/>
      <div style={{flex:1}}>
        <div style={{width:"100%",height:8,borderRadius:4,background:theme.divider,overflow:"hidden"}}>
          <div style={{height:"100%",borderRadius:4,background:theme.greenText,width:`${Math.round(stableCount/Math.max(allKeys.length,1)*100)}%`}}/>
        </div>
        <div style={{fontSize:11,color:theme.text3,marginTop:4}}>{Math.round(stableCount/Math.max(allKeys.length,1)*100)}% Konsensus</div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:11,color:theme.text3}}>Top</span>
        {[10,15,20,25].map(n => (
          <button key={n} onClick={()=>setTopN(n)}
            style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${topN===n?theme.blueText:theme.border}`,
              background:topN===n?theme.blueLight:theme.white,color:topN===n?theme.blueText:theme.text3,
              fontSize:12,fontWeight:topN===n?700:400,cursor:"pointer",fontFamily:fonts.mono}}>
            {n}
          </button>
        ))}
        <span style={{fontSize:11,color:theme.text4}}>nach Kombi-Score</span>
      </div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      {groups.map(grp => {
        const keys = grp.keys.filter(k=>allKeys.includes(k));
        if (!keys.length) return null;
        const grpStable = keys.filter(k=>paramStats[k] && paramStats[k].allSame).length;
        return <Card key={grp.label} style={{overflow:"hidden"}}>
          <div style={{padding:"8px 14px",borderBottom:`1px solid ${grp.color}22`,display:"flex",alignItems:"center",gap:8,background:`${grp.color}08`}}>
            <span style={{fontSize:15}}>{grp.icon}</span>
            <span style={{fontSize:13,fontWeight:700,color:grp.color}}>{grp.label}</span>
            <span style={{marginLeft:"auto",fontSize:11,color:theme.text4}}>{grpStable}/{keys.length} stabil</span>
          </div>
          {keys.map(k => {
            const ps = paramStats[k];
            if (!ps) return null;
            const stab = Math.round(ps.consensus*100);
            const stabColor = stab>=80?theme.greenText:stab>=50?theme.amberText:theme.redText;
            const isToggle = ps.consensusVal==="true"||ps.consensusVal==="false";
            return <div key={k} style={{padding:"8px 14px",borderBottom:`1px solid ${theme.divider}`,display:"flex",alignItems:"center",gap:10,background:ps.allSame?"rgba(16,185,129,0.02)":"transparent"}}>
              <div style={{minWidth:150,flex:"0 0 150px"}}>
                <div style={{fontSize:12,fontWeight:ps.allSame?700:500,color:theme.text}}>{k}</div>
                {ps.allSame && <div style={{fontSize:9,color:theme.greenText,fontWeight:700,textTransform:"uppercase"}}>stabil</div>}
              </div>
              <div style={{minWidth:65,textAlign:"center"}}>
                <div style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:isToggle?(ps.consensusVal==="true"?theme.greenText:theme.redText):ps.allSame?theme.greenText:theme.text}}>
                  {ps.consensusVal||"-"}
                </div>
              </div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                  <div style={{flex:1,height:5,borderRadius:3,background:theme.divider,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:3,background:stabColor,width:`${stab}%`}}/>
                  </div>
                  <span style={{fontSize:10,fontFamily:fonts.mono,color:stabColor,fontWeight:700,minWidth:32}}>{stab}%</span>
                </div>
                {!ps.allSame && ps.topValues && ps.topValues.length>1 && (
                  <div style={{display:"flex",gap:4,marginTop:3,flexWrap:"wrap"}}>
                    {ps.topValues.map(tv=>(
                      <span key={tv[0]} style={{fontSize:11,padding:"2px 8px",borderRadius:4,
                        background:theme.tint,color:theme.text2,fontFamily:fonts.mono,fontWeight:600,
                        border:`1px solid ${theme.divider}`}}>
                        {tv[0]} <span style={{color:theme.text4,fontWeight:400}}>×{tv[1]}</span>
                      </span>
                    ))}
                  </div>
                )}
                {ps.min!=null && ps.max!=null && ps.min!==ps.max && (
                  <div style={{fontSize:11,color:theme.text4,fontFamily:fonts.mono,marginTop:2}}>
                    Range: {Number(ps.min).toFixed(2)} – {Number(ps.max).toFixed(2)}
                  </div>
                )}
              </div>
            </div>;
          })}
        </Card>;
      })}
    </div>
  </>;
}

export function OptDetail({ runId, onClose, onApply, onApplyConsensus, stratFile }) {
  const [data,setData]=useState(null); const [ld,setLd]=useState(true); const [tab,setTab]=useState("overview");
  const [sort,setSort]=useState({key:"compositeScore",dir:"desc"}); const [selT,setSelT]=useState(null);
  const [selPareto, setSelPareto] = useState(null);
  const [manualBestTrial, setManualBestTrial] = useState(null); // user-selected "best" trial
  // v4-FIX: Pagination + filter state for large trial lists (1000+ trials crash without this)
  const [trialFilter, setTrialFilter] = useState("");
  const [trialPageSize, setTrialPageSize] = useState(50);
  const [trialShowGradeA, setTrialShowGradeA] = useState(false);
  const [trialHideDQ, setTrialHideDQ] = useState(true);
  const [showAdvFilter, setShowAdvFilter] = useState(false);
  const [advFilter, setAdvFilter] = useState({
    minPnl:"", maxPnl:"", minPf:"", maxDd:"",
    minSharpe:"", minWr:"", minTrades:"", maxTrades:""
  });
  useEffect(() => { if(!runId) return; setLd(true); getApi(`/results/optimizations/${runId}`).then(d => { setData(d); setLd(false); }); }, [runId]);
  if (ld) return <SkeletonCards n={4}/>;
  if (!data) return <Card style={{ padding:32, textAlign:"center" }}><div style={{ color:theme.redText }}>Not found</div><Button onClick={onClose} style={{ marginTop:12 }}>Back</Button></Card>;
  const trials = data.trial_history||[]; const bp = data.best_params||{}; const totalTrials = data.trials_completed||trials.length;
  const paretoFront = data.pareto_front || [];
  const paretoAxes  = data.pareto_axes  || ["net_pnl", "max_drawdown_usd"];
  const isPareto    = paretoFront.length > 0;

  // Enrich trial records with compositeScore (same formula as index.jsx overview)
  const validT = trials.filter(t => !t.is_disqualified);
  if (validT.length > 0 && validT[0].compositeScore == null) {
    const rank = (arr, key, desc=true) => new Map(
      [...arr].sort((a,b) => desc ? (b[key]||0)-(a[key]||0) : (a[key]||0)-(b[key]||0))
               .map((t,i) => [t.trial, i])
    );
    const pnlR=rank(validT,"net_pnl"), shR=rank(validT,"sharpe"),
          ddR=rank(validT,"max_drawdown",false), pfR=rank(validT,"profit_factor"),
          wrR=rank(validT,"win_rate");
    const n = validT.length || 1;
    validT.forEach(t => {
      t.compositeScore = Math.round((
        (1-(pnlR.get(t.trial)||0)/n)*0.30 + (1-(shR.get(t.trial)||0)/n)*0.25 +
        (1-(ddR.get(t.trial)||0)/n)*0.20 + (1-(pfR.get(t.trial)||0)/n)*0.15 +
        (1-(wrR.get(t.trial)||0)/n)*0.10
      )*100);
    });
  }
  // v5: Disqualified trials (violate hard_constraints) are excluded from ALL aggregate metrics
  // and "best trial" selection. They remain visible in the detailed trials-table with DQ-filter toggle.
  const validTrials = trials.filter(t => !t.is_disqualified);
  const disqCount = trials.length - validTrials.length;
  // ── Filter helpers at component level ──
  const applyAdvWithOp = (t) => {
    const af = advFilter;
    const v = (x) => x === "" || x == null;
    if (!v(af.minPnl)    && (t.net_pnl||0)        < Number(af.minPnl))    return false;
    if (!v(af.maxPnl)    && (t.net_pnl||0)        > Number(af.maxPnl))    return false;
    if (!v(af.minPf)     && (t.profit_factor||0)  < Number(af.minPf))     return false;
    if (!v(af.maxDd)     && (t.max_drawdown||0)   > Number(af.maxDd))     return false;
    if (!v(af.minSharpe) && (t.sharpe||0)         < Number(af.minSharpe)) return false;
    if (!v(af.minWr)     && (t.win_rate||0)        < Number(af.minWr))     return false;
    if (!v(af.minTrades) && (t.total_trades||0)   < Number(af.minTrades)) return false;
    if (!v(af.maxTrades) && (t.total_trades||0)   > Number(af.maxTrades)) return false;
    return true;
  };
  const renderAF = (label, field, placeholder, isMax=false) => {
    const af = advFilter;
    const active = af[field] !== "" && af[field] != null;
    return (
      <div key={field} style={{display:"flex",flexDirection:"column",gap:3}}>
        <div style={{fontSize:10,color:active?theme.blueText:theme.text4,fontWeight:600,textTransform:"uppercase",letterSpacing:".5px"}}>{label}</div>
        <div style={{position:"relative"}}>
          <span style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",fontSize:11,color:theme.text3,pointerEvents:"none",fontFamily:fonts.mono}}>{isMax?"≤":"≥"}</span>
          <input type="number" placeholder={placeholder} value={af[field]??""}
            onChange={e => { e.stopPropagation(); setAdvFilter(p=>({...p,[field]:e.target.value})); }}
            onKeyDown={e => e.stopPropagation()} onKeyUp={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            style={{width:"100%", padding:"6px 8px 6px 22px", borderRadius:6,
              border:`1.5px solid ${active?theme.blueText:theme.border}`,
              fontSize:12, fontFamily:fonts.mono, background:active?"rgba(37,99,235,0.04)":theme.white,
              color:theme.text, outline:"none"}}/>
        </div>
      </div>
    );
  };
  const bestTrial = validT.length > 0
    ? [...validT].sort((a,b) => (b.compositeScore||0) - (a.compositeScore||0))[0]
    : null;
  const bestPnl = data.best_net_pnl || bestTrial?.net_pnl || 0;
  const bestGrade = data.best_grade || bestTrial?.grade || "?";
  // Sprint K: WF-Obj-spezifische PnL-Werte aus bestTrial holen
  const optModeForDetail = data.optimization_mode || data.goals?.optimization_mode || "standard";
  // Sprint K Frontend Fix: Detect WF-Obj aus bestTrial-Daten (mode-marker fehlt im Backend-Status)
  const isWfObjDetail = optModeForDetail === "wf_objective"
    || bestTrial?.wf_obj_extrapolated_pnl != null
    || (data.trial_history || []).some(t => t.wf_obj_extrapolated_pnl != null);
  const wfObjAvgPerWindow   = bestTrial?.wf_obj_avg_per_window ?? bestTrial?.net_pnl;
  const wfObjTotalOosSum    = bestTrial?.wf_obj_total_oos_sum;
  const wfObjExtrapolated   = bestTrial?.wf_obj_extrapolated_pnl;
  const wfObjWindowCount    = bestTrial?.wf_obj_oos_window_count;
  const wfObjOosTotalDays   = bestTrial?.wf_obj_oos_total_days;
  const wfObjFullPeriodDays = bestTrial?.wf_obj_full_period_days;
  // R7f-2: Detect whether consensus is available for this historical run
  const hasConsensus = !!(data.analysis && onApplyConsensus && stratFile);
  const paramGroups = bp && Object.keys(bp).length > 0 ? [
    // R5-10: Align keys with actual optimizer output (constants.js bestParamsToApplyData)
    {title:"Take Profit",params:["tp_mode","num_tp_levels","rr1","rr2","rr3","rr4","partial_pcts","partial_trail_mode","trail_after_tp"],icon:"🎯"},
    {title:"Stop Loss",params:["sl_mode","sl_atr_mult_min","sl_atr_mult_max","atr_length","sl_fixed_points","sl_ema_length"],icon:"🛡️"},
    {title:"Structure",params:["context_swing_length","entry_swing_length","max_pattern_age","context_choch_max_age","break_confirm","entry_mode","require_htf_choch","require_htf_trend","direction_filter"],icon:"🔧"},
    {title:"Regime & Filters",params:["regime_enabled","regime_mode","adx_threshold","atr_sma_length","ema_enabled","ema_mode","ctx_ema1_length","entry_ema1_length","fvg_enabled","fvg_proximity_enabled"],icon:"📊"},
    {title:"Risk Mgmt",params:["trail_mode","trail_atr_mult","trail_activation_rr","be_enabled","be_mode","be_trigger_rr","loss_scaling_enabled","max_daily_trades","cooldown_bars","max_bars_in_trade"],icon:"⚠️"},
  ] : [];
  const sortedTrials = [...trials].sort((a,b) => {
    const key = sort.key;
    // compositeScore is computed client-side — use it directly
    const va = key === "score" ? (a.compositeScore ?? a.score ?? 0) : (a[key] ?? 0);
    const vb = key === "score" ? (b.compositeScore ?? b.score ?? 0) : (b[key] ?? 0);
    return sort.dir === "asc" ? va - vb : vb - va;
  });
  const toggleSort = k => setSort(s => ({key:k,dir:s.key===k&&s.dir==="desc"?"asc":"desc"}));
  const SH = ({k,children}) => <th onClick={() => toggleSort(k)} style={{ padding:"6px 8px", fontSize:9, fontWeight:700, color:sort.key===k?theme.blueText:theme.text4, textTransform:"uppercase", textAlign:"left", cursor:"pointer", userSelect:"none" }}>{children}{sort.key===k ? sort.dir==="desc"?" ▼":" ▲" : ""}</th>;

  // R7m-3: Extract TFs for header display. Priority: best_params (Phase 1 TF-Search) → summary → bestTrial
  const _e = bp.entry_tf || data.entry_tf || bestTrial?.params?.entry_tf || "";
  const _c = bp.context_tf || data.context_tf || bestTrial?.params?.context_tf || "";
  const _r = bp.regime_tf || data.regime_tf || bestTrial?.params?.regime_tf || "";
  const tfLabel = (_e || _c || _r) ? (
    <span style={{padding:"2px 8px",borderRadius:5,background:theme.blueLight,color:theme.blueText,fontFamily:fonts.mono,fontSize:10,fontWeight:700,letterSpacing:"0.3px"}}>
      {_e && <>E:{_e}</>}
      {_e && _c && <span style={{opacity:0.4}}> / </span>}
      {_c && <>C:{_c}</>}
      {_c && _r && <span style={{opacity:0.4}}> / </span>}
      {_r && <>R:{_r}</>}
    </span>
  ) : null;

  return <div>
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
      <BackBtn onClick={onClose}/>
      <div>
        <div style={{ fontSize:18, fontWeight:900, display:"flex", alignItems:"center", gap:10 }}>
          Optimization #{runId} <span style={{ fontSize:12, fontWeight:500, color:theme.text3 }}>{data.instrument||""}</span>
          {tfLabel}
        </div>
        <div style={{ fontSize:11, color:theme.text3, fontFamily:fonts.mono }}>{totalTrials} trials · {data.objective||"?"} · {Math.round((data.elapsed_seconds||0)/60)} min · {data.mode||"tuning"}</div>
      </div>
      <div style={{ marginLeft:"auto", textAlign:"right" }}>
        <div style={{ fontSize:24, fontWeight:900, fontFamily:fonts.mono, color:bestPnl>0?theme.greenText:theme.redText }}>{formatPnl(bestPnl)}</div>
        {bestGrade !== "?" && <Pill bg={bestGrade==="A"?theme.greenLight:theme.blueLight} color={bestGrade==="A"?theme.greenText:theme.blueText}>Grade {bestGrade}</Pill>}
      </div>
    </div>

    {/* R7f-2: Apply Action Bar — always visible below header, on all tabs */}
    {(hasConsensus || (onApply && Object.keys(bp).length > 0)) && (
      <div style={{
        display:"flex", alignItems:"center", gap:10, marginBottom:16,
        padding:"12px 16px", borderRadius:10,
        background: hasConsensus ? `${theme.green}0D` : theme.tint,
        border: `1px solid ${hasConsensus ? theme.green+"33" : theme.divider}`,
      }}>
        <div style={{ flex:1 }}>
          {hasConsensus ? <>
            <div style={{ fontSize:12, fontWeight:700, color:theme.greenText, marginBottom:2 }}>
              ✨ Empfohlen: Apply Consensus
            </div>
            <div style={{ fontSize:11, color:theme.text3 }}>
              Nutzt die statistische Verteilung aller Grade-A Trials. Robuster als einen einzelnen Best-Trial zu wählen.
            </div>
          </> : <>
            <div style={{ fontSize:12, fontWeight:700, color:theme.text2, marginBottom:2 }}>
              Best-Trial übernehmen
            </div>
            <div style={{ fontSize:11, color:theme.text3 }}>
              Übernimmt die Parameter des Trials mit höchstem Score direkt in {stratFile||"die YAML"}.
            </div>
          </>}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {onApply && Object.keys(bp).length > 0 && (
            <Button className="sl-btn" onClick={() => onApply(bestParamsToApplyData(bp))}
              style={{ padding:"10px 18px", fontSize:12, fontWeight:700 }}>
              Apply Best Trial
            </Button>
          )}
          {hasConsensus && (
            <Button primary className="sl-btn" onClick={() => onApplyConsensus(
              data.analysis,
              data.mode,
              data.best_params,
              data.best_net_pnl || bestTrial?.net_pnl || 0,
              data.trial_history
            )}
              style={{ background:theme.green, padding:"10px 22px", fontSize:13, fontWeight:800 }}>
              ✨ Apply Consensus
            </Button>
          )}
        </div>
      </div>
    )}

    <div style={{ display:"flex", gap:3, padding:3, borderRadius:8, background:theme.tint, marginBottom:18, width:"fit-content" }}>
      {["overview","trials","params","analysis",...((data.mtf_regime_data || (data.mtf_regime_data_per_trial && Object.keys(data.mtf_regime_data_per_trial).length>0))?["regime"]:[]),...(isPareto?["pareto"]:[])].map(t => <button key={t} onClick={() => { setTab(t); setSelT(null); }} style={{ padding:"5px 13px", borderRadius:6, border:"none", cursor:"pointer", fontSize:11, fontWeight:tab===t?700:500, background:tab===t?theme.white:"transparent", color:tab===t?theme.text:theme.text3, boxShadow:tab===t?theme.shadow1:"none", fontFamily:fonts.body, textTransform:"capitalize" }}>{t==="analysis"?"📊 Analysis":t==="regime"?"🌊 Regime":t==="pareto"?"🎯 Pareto":t}</button>)}
    </div>

    {tab==="overview" && <>
      {/* ── Interpretation Panel ── */}
      {trials.length > 0 && (() => {
        // v5: aggregate metrics exclude disqualified trials (they violate hard constraints)
        const gradeA = validTrials.filter(t=>t.grade==="A").length;
        const gradeB = validTrials.filter(t=>t.grade==="B").length;
        const profitable = validTrials.filter(t=>t.net_pnl>0).length;
        const avgPnl = validTrials.length > 0 ? validTrials.reduce((s,t)=>s+(t.net_pnl||0),0)/validTrials.length : 0;
        const maxPnl = validTrials.length > 0 ? Math.max(...validTrials.map(t=>t.net_pnl||0)) : 0;
        const validCount = validTrials.length;
        const disqPct = totalTrials > 0 ? (disqCount/totalTrials)*100 : 0;
        const ins = [];
        // v5: Disqualified-Share Warnung IMMER oben, weil es eine Deploy-Blocker-Info ist
        if (disqCount > 0) {
          if (disqPct >= 50) {
            ins.push({icon:"🚨",text:`${disqCount}/${totalTrials} Trials (${disqPct.toFixed(0)}%) disqualifiziert — Hard-Constraints werden MASSIV verletzt. Deploy blockiert. Strategy-Logik oder Constraints grundlegend überprüfen.`,color:theme.redText});
          } else if (disqPct >= 25) {
            ins.push({icon:"⚠️",text:`${disqCount}/${totalTrials} Trials (${disqPct.toFixed(0)}%) disqualifiziert — viele Parameter-Kombis verletzen max_dd oder min_pnl. Vor Deploy Constraints prüfen.`,color:theme.amberText});
          } else {
            ins.push({icon:"ℹ️",text:`${disqCount}/${totalTrials} Trials (${disqPct.toFixed(0)}%) disqualifiziert — im akzeptablen Rahmen. Nur valide Trials in den Metriken unten.`,color:theme.text2});
          }
        }
        // Grade Assessment (nur valide)
        if (gradeA >= 5) ins.push({icon:"✅",text:`${gradeA} Grade-A Trials (${((gradeA/validCount)*100).toFixed(0)}% der validen) — starke, robuste Ergebnisse. Hohe Konfidenz in den besten Parametern.`,color:theme.greenText});
        else if (gradeA >= 1) ins.push({icon:"⚠️",text:`${gradeA} Grade-A Trial(s) bei ${validCount} validen — brauchbares Ergebnis, aber Vorsicht vor Overfitting.`,color:theme.amberText});
        else ins.push({icon:"❌",text:`Kein Grade-A Trial unter ${validCount} validen — Parameterkombination für dieses Instrument möglicherweise nicht optimal.`,color:theme.redText});
        // Profitability (nur valide)
        ins.push({icon:"💰",text:`${((profitable/Math.max(validCount,1))*100).toFixed(0)}% der validen Trials profitabel (${profitable}/${validCount}). Ø P&L: ${formatPnl(avgPnl)}. Bester P&L: ${formatPnl(maxPnl)}.`});
        // Key Parameters


        // Feature insights — trail_mode is categorical ("none"/"atr_trail"/etc), not a boolean
        const disabledFeatures = [bp.regime_enabled===false&&"Regime",bp.ema_enabled===false&&"EMA",bp.fvg_enabled===false&&"FVG",bp.trail_mode==="none"&&"Trailing",bp.be_enabled===false&&"Break-Even",bp.loss_scaling_enabled===false&&"Loss Scaling"].filter(Boolean);
        // v4: exploration-phase disabled-features narration removed (single mode only)
        // Convergence (auf validen berechnen für faire Messung)
        if (validTrials.length > 100) {
          const last50 = validTrials.slice(-50);
          const first50 = validTrials.slice(0,50);
          const last50Avg = last50.reduce((s,t)=>s+(t.score||0),0)/50;
          const first50Avg = first50.reduce((s,t)=>s+(t.score||0),0)/50;
          if (last50Avg > first50Avg * 1.3) ins.push({icon:"📈",text:`Optimizer konvergiert gut — letzte 50 valide Trials im Schnitt ${((last50Avg/first50Avg-1)*100).toFixed(0)}% besser als die ersten 50.`});
        }


        return <Card style={{ marginBottom:18, padding:"18px 22px", background:"rgba(37,99,235,0.02)", border:`1px solid rgba(37,99,235,0.08)` }} className="sl-fadein">
          <div style={{ fontSize:15, fontWeight:800, color:theme.text, marginBottom:12 }}>📋 Run-Analyse</div>
          {ins.map((item,i) => <div key={i} style={{ display:"flex", gap:10, padding:"7px 0", borderBottom:i<ins.length-1?`1px solid ${theme.divider}`:"none" }}>
            <span style={{ fontSize:15, flexShrink:0 }}>{item.icon}</span>
            <span style={{ fontSize:13, color:item.color||theme.text2, lineHeight:1.6 }}>{item.text}</span>
          </div>)}
        </Card>;
      })()}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:8, marginBottom:18 }}>
        {(isWfObjDetail && wfObjExtrapolated != null
          ? [
              ["Avg/Window", formatPnl(wfObjAvgPerWindow||0), (wfObjAvgPerWindow||0)>0?theme.greenText:theme.redText, `Durchschnitt pro OOS-Fenster (${wfObjWindowCount||0} Fenster · ${wfObjOosTotalDays||0} Tage)`],
              ["Sum OOS",    formatPnl(wfObjTotalOosSum||0),  (wfObjTotalOosSum||0)>0?theme.greenText:theme.redText, `Tatsächliche Summe aller OOS-Fenster (${wfObjOosTotalDays||0} Tage gesamt)`],
              ["Extrapoliert", formatPnl(wfObjExtrapolated||0), (wfObjExtrapolated||0)>0?theme.greenText:theme.redText, `Linear hochgerechnet auf volle Periode (${wfObjFullPeriodDays||0} Tage) — vergleichbar mit Standard-IS-PnL`],
              ["Grade", bestGrade, bestGrade==="A"?theme.greenText:theme.amberText, "OOS-basiertes Grade"],
              ["Trials", totalTrials, theme.text, "Anzahl Bayesian-Trials"],
              ["Elapsed", `${Math.round((data.elapsed_seconds||0)/60)}min`, theme.text3, "Laufzeit"],
            ]
          : [
              ["Best P&L", formatPnl(bestPnl), bestPnl>0?theme.greenText:theme.redText, null],
              ["Grade", bestGrade, bestGrade==="A"?theme.greenText:theme.amberText, null],
              ["Trials", totalTrials, theme.text, null],
              ["Elapsed", `${Math.round((data.elapsed_seconds||0)/60)}min`, theme.text3, null],
            ]
        ).map(([l,v,c,tooltip]) =>
          <Card key={l} className="sl-card sl-fadein" style={{ padding:"10px 14px" }} title={tooltip||undefined}>
            <div style={{ fontSize:8.5, fontWeight:700, color:theme.text4, textTransform:"uppercase", letterSpacing:".6px", marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:15, fontWeight:800, fontFamily:fonts.mono, color:c }}>{v}</div>
          </Card>
        )}
      </div>
      {paramGroups.length > 0 && <><SectionTitle>Best Parameters</SectionTitle>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12, marginBottom:18 }}>
          {paramGroups.map(g => { const params=g.params.filter(k=>bp[k]!=null); if(!params.length) return null;
            return <Card key={g.title} className="sl-card">
              <div style={{ padding:"10px 14px", borderBottom:`1px solid ${theme.divider}`, fontSize:12, fontWeight:700 }}>{g.icon} {g.title}</div>
              <div style={{ padding:"8px 14px" }}>{params.map((k,i) =>
                <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:i<params.length-1?`1px solid ${theme.divider}`:"none" }}>
                  <span style={{ fontSize:11, color:theme.text2 }}>{k.replace(/_/g," ")}</span>
                  <span style={{ fontSize:12, fontWeight:700, fontFamily:fonts.mono, color:typeof bp[k]==="boolean"?(bp[k]?theme.greenText:theme.redText):theme.text }}>{typeof bp[k]==="number"?bp[k].toFixed(2):String(bp[k])}</span>
                </div>
              )}</div>
            </Card>;
          })}
        </div>
        {/* R7f-3: Legacy Apply button removed — new action bar at top handles this on all tabs */}
      </>}
    </>}

    {tab==="trials" && <>
      {/* ── Manual Best Banner ── */}
      {manualBestTrial && <div style={{ marginBottom:12, padding:"10px 16px", borderRadius:8, background:"rgba(245,158,11,0.1)", border:"1.5px solid #f59e0b", display:"flex", alignItems:"center", gap:12 }}>
        <span style={{ fontSize:18 }}>★</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#d97706" }}>Manuell gewählter Best Trial: #{manualBestTrial.trial}</div>
          <div style={{ fontSize:11, color:theme.text3 }}>
            P&L {formatPnl(manualBestTrial.net_pnl)} · Kombi {manualBestTrial.compositeScore!=null?manualBestTrial.compositeScore+"%":Number(manualBestTrial.score||0).toFixed(2)} · Sharpe {Number(manualBestTrial.sharpe||0).toFixed(2)} · DD {(manualBestTrial.max_drawdown||0).toFixed(1)}%
            &nbsp;— <b>Score-Ranking ignoriert</b>. Dieser Trial wird beim Apply verwendet.
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {onApply && manualBestTrial.params && <Button primary onClick={() => onApply(bestParamsToApplyData(manualBestTrial.params))}>Apply Trial #{manualBestTrial.trial}</Button>}
          <Button onClick={() => setManualBestTrial(null)}>Zurücksetzen</Button>
        </div>
      </div>}

      {selT && <Card style={{ marginBottom:14, border:`2px solid ${theme.blue}` }} className="sl-fadein">
        <CardHeader title={`Trial #${selT.trial}`} sub={`Kombi: ${selT.compositeScore!=null?selT.compositeScore+"%":"—"} · Bayesian: ${Number(selT.score||0).toFixed(2)} · Grade: ${selT.grade||"?"}`} right={<Button onClick={() => setSelT(null)}>Schließen</Button>}/>
        <div style={{ padding:"12px 20px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8, marginBottom:14 }}>
            {[["P&L",formatPnl(selT.net_pnl),selT.net_pnl>0?theme.greenText:theme.redText],["PF",Number(selT.profit_factor||0).toFixed(2)],["Sharpe",Number(selT.sharpe||0).toFixed(2)],["WR",`${(selT.win_rate||0).toFixed(1)}%`],["DD",`${(selT.max_drawdown||0).toFixed(1)}%`,theme.redText],["Trades",selT.total_trades||0]].map(([l,v,c]) =>
              <div key={l}><div style={{ fontSize:8.5, fontWeight:700, color:theme.text4, textTransform:"uppercase" }}>{l}</div><div style={{ fontSize:14, fontWeight:800, fontFamily:fonts.mono, color:c||theme.text }}>{v}</div></div>
            )}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:4, maxHeight:250, overflow:"auto" }}>
            {selT.params && Object.entries(selT.params).map(([k,v]) =>
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"4px 8px", borderRadius:4, background:theme.tint }}>
                <span style={{ fontSize:10.5, color:theme.text3 }}>{k}</span>
                <span style={{ fontSize:10.5, fontWeight:700, fontFamily:fonts.mono, color:typeof v==="boolean"?(v?theme.greenText:theme.redText):theme.text }}>{typeof v==="number"?v.toFixed(3):String(v)}</span>
              </div>
            )}
          </div>
        </div>
      </Card>}
      {/* v4-FIX: Pagination + filter to avoid crash on 1000+ trials rendering 15k+ cells */}
      {(() => {
        // Filter and pagination state
        const q = (trialFilter || "").toLowerCase();
        // Advanced filter
        const af = advFilter;

        const filtered = sortedTrials.filter(t => {
          if (trialShowGradeA && (t.compositeScore??0) < 70) return false;
          if (trialHideDQ && t.is_disqualified) return false;
          if (!applyAdvWithOp(t)) return false;
          if (q) {
            const hay = `${t.trial} ${t.grade||""} ${(t.params?.entry_mode||"")} ${(t.params?.entry_tf||"")}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        });
        const totalShown = filtered.length;
        const visible = filtered.slice(0, trialPageSize);





        return <Card>
          <CardHeader title="All Trials"
            sub={`${totalShown} / ${trials.length} ${totalShown !== trials.length ? "(gefiltert)" : ""} · Klicke für Details`}/>
          <div style={{ padding:"10px 16px", borderBottom:`1px solid ${theme.divider}` }}>
            <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom: showAdvFilter ? 10 : 0 }}>
              <input type="text" value={trialFilter} onChange={e=>setTrialFilter(e.target.value)}
                onKeyDown={e=>e.stopPropagation()} onKeyUp={e=>e.stopPropagation()} onClick={e=>e.stopPropagation()}
                placeholder="Filter: Trial# / entry_mode..."
                style={{ padding:"7px 12px", fontSize:13, border:`1px solid ${theme.border}`, borderRadius:6, width:260, fontFamily:fonts.body, outline:"none" }}/>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer", color:theme.text2 }}>
                <input type="checkbox" checked={trialShowGradeA} onChange={e=>setTrialShowGradeA(e.target.checked)}/> Grade A+
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer", color:theme.text2 }}>
                <input type="checkbox" checked={trialHideDQ} onChange={e=>setTrialHideDQ(e.target.checked)}/> DQ verstecken
              </label>
              <button onClick={()=>setShowAdvFilter(s=>!s)}
                style={{ padding:"6px 12px", fontSize:12, borderRadius:6, border:`1px solid ${showAdvFilter?theme.blueText:theme.border}`, background:showAdvFilter?theme.blueLight:theme.white, color:showAdvFilter?theme.blueText:theme.text2, cursor:"pointer", fontWeight:600 }}>
                ⚙ Filter{Object.values(af).some(v=>v!=="") ? " (aktiv)" : ""}
              </button>
              {Object.values(af).some(v=>v!=="") && <button onClick={()=>setAdvFilter({minPnl:"",maxPnl:"",minPf:"",maxDd:"",minSharpe:"",minWr:"",minTrades:"",maxTrades:"",minPnlOp:">=",maxPnlOp:"<=",minPfOp:">=",maxDdOp:"<=",minSharpeOp:">=",minWrOp:">=",minTradesOp:">=",maxTradesOp:"<="})}
                style={{ padding:"6px 10px", fontSize:11, borderRadius:6, border:`1px solid ${theme.border}`, background:theme.tint, color:theme.text3, cursor:"pointer" }}>✕</button>}
              <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center" }}>
                <span style={{ fontSize:11, color:theme.text3 }}>Zeige</span>
                <select value={trialPageSize} onChange={e=>setTrialPageSize(parseInt(e.target.value))}
                  style={{ padding:"5px 8px", fontSize:12, border:`1px solid ${theme.border}`, borderRadius:5, background:theme.white, cursor:"pointer" }}>
                  {[25,50,100,200,500].map(n=><option key={n} value={n}>{n}</option>)}
                </select>
                <span style={{ fontSize:11, color:theme.text3 }}>von {totalShown}</span>
              </div>
            </div>
            {showAdvFilter && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(8,1fr)", gap:8, paddingTop:8 }}>
                {renderAF("Min P&L", "minPnl", "50.000")}
                {renderAF("Max P&L", "maxPnl", "200.000", true)}
                {renderAF("Min PF", "minPf", "1.3")}
                {renderAF("Max DD%", "maxDd", "10", true)}
                {renderAF("Min Sharpe", "minSharpe", "2.0")}
                {renderAF("Min WR%", "minWr", "40")}
                {renderAF("Min Trades", "minTrades", "300")}
                {renderAF("Max Trades", "maxTrades", "800", true)}
              </div>
            )}
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ borderBottom:`2px solid ${theme.divider}`, background:theme.tint, position:"sticky", top:0, zIndex:1 }}>
                <th style={{padding:"8px 10px",fontSize:10,fontWeight:700,color:theme.text4,textAlign:"left"}}>#</th>
                {[["compositeScore","Kombi"],["net_pnl","P&L"],["profit_factor","PF"],["sharpe","Sharpe"],["win_rate","WR %"],["max_drawdown","DD %"],["total_trades","Trades"],["stability_score","Stabil."]].map(([k,h])=>(
                  <th key={k} onClick={()=>setSort(s=>({key:k,dir:s.key===k&&s.dir==="desc"?"asc":"desc"}))}
                    style={{padding:"8px 10px",fontSize:10,fontWeight:700,color:sort.key===k?theme.blueText:theme.text4,textTransform:"uppercase",textAlign:"left",cursor:"pointer",whiteSpace:"nowrap",userSelect:"none"}}>
                    {h}{sort.key===k?(sort.dir==="desc"?" ▼":" ▲"):""}
                  </th>
                ))}
                <th style={{padding:"8px 10px",fontSize:10,fontWeight:700,color:theme.text4,textAlign:"left"}}>Grade</th>
                {onApply && <th style={{padding:"8px 10px",fontSize:9,color:theme.text4,textAlign:"right"}}>Action</th>}
              </tr></thead>
              <tbody>{visible.map((t) => {
                const cs = t.compositeScore ?? 0;
                const grade = cs>=70?"A":cs>=50?"B":cs>=35?"C":"D";
                const gradeColor = cs>=70?theme.greenText:cs>=50?theme.blueText:cs>=35?theme.amberText:theme.text3;
                const dqReasons = [...(t.constraint_violations||[]), ...(t.flags||[]).filter(f=>f.includes("<")||f.includes(">"))];
                return [
                  <tr key={t.trial} className="sl-row"
                    onClick={()=>setSelT(selT?.trial===t.trial?null:t)}
                    style={{ borderBottom:`1px solid ${theme.divider}`, cursor:"pointer",
                             background:selT?.trial===t.trial?theme.blueLight:t.is_disqualified?"rgba(239,68,68,0.03)":"transparent",
                             opacity:t.is_disqualified?0.6:1 }}>
                    <td style={{padding:"8px 10px",fontSize:12,fontFamily:fonts.mono,color:theme.text3,whiteSpace:"nowrap"}}>
                      {t.trial}
                      {t.is_disqualified&&<span style={{marginLeft:4,fontSize:9,padding:"1px 4px",borderRadius:3,background:"rgba(239,68,68,0.15)",color:theme.redText,fontWeight:700}}>DQ</span>}
                    </td>
                    <td style={{padding:"8px 10px",fontSize:14,fontWeight:800,fontFamily:fonts.mono,color:cs>=70?theme.greenText:cs>=50?theme.amberText:theme.text3}}>{cs>0?`${cs}%`:"—"}</td>
                    <td style={{padding:"8px 10px",fontSize:13,fontWeight:700,fontFamily:fonts.mono,color:(t.net_pnl||0)>=0?theme.greenText:theme.redText}}>{formatPnl(t.net_pnl)}</td>
                    <td style={{padding:"8px 10px",fontSize:13,fontFamily:fonts.mono,color:(t.profit_factor||0)>=1.5?theme.greenText:theme.text}}>{(t.profit_factor||0).toFixed(2)}</td>
                    <td style={{padding:"8px 10px",fontSize:13,fontFamily:fonts.mono,color:(t.sharpe||0)>=2?theme.greenText:theme.text}}>{(t.sharpe||0).toFixed(2)}</td>
                    <td style={{padding:"8px 10px",fontSize:13,fontFamily:fonts.mono}}>{(t.win_rate||0).toFixed(1)}</td>
                    <td style={{padding:"8px 10px",fontSize:13,fontFamily:fonts.mono,color:(t.max_drawdown||0)>10?theme.redText:theme.text,fontWeight:(t.max_drawdown||0)>10?700:400}}>{(t.max_drawdown||0).toFixed(1)}</td>
                    <td style={{padding:"8px 10px",fontSize:13,fontFamily:fonts.mono}}>{t.total_trades||0}</td>
                    <td style={{padding:"8px 10px",fontSize:12,fontFamily:fonts.mono,
                      color:t.stability_score!=null?(t.stability_score>=0.6?theme.greenText:t.stability_score>=0.3?theme.amberText:theme.redText):theme.text4}}
                      title={t.stability_score!=null?`${t.stability_passed||"?"}/${t.stability_tested||"?"} Nachbarn valid`:"Nicht geprüft"}>
                      {t.stability_score!=null?`${(t.stability_score*100).toFixed(0)}%`:"—"}
                    </td>
                    <td style={{padding:"8px 10px"}}><Pill bg={cs>=70?theme.greenLight:cs>=50?theme.blueLight:cs>=35?"rgba(245,158,11,0.1)":"transparent"} color={gradeColor}>{grade}</Pill></td>
                    {onApply && <td style={{padding:"8px 8px",textAlign:"right"}}>
                      <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
                        <button onClick={e=>{e.stopPropagation();setManualBestTrial(prev=>prev?.trial===t.trial?null:t);}}
                          style={{fontSize:11,padding:"3px 7px",borderRadius:4,border:`1px solid ${manualBestTrial?.trial===t.trial?"#f59e0b":"rgba(245,158,11,0.2)"}`,background:manualBestTrial?.trial===t.trial?"rgba(245,158,11,0.15)":theme.tint,color:manualBestTrial?.trial===t.trial?"#d97706":theme.text4,cursor:"pointer",fontWeight:700}}>
                          {manualBestTrial?.trial===t.trial?"★":"☆"}
                        </button>
                        {t.params&&Object.keys(t.params).length>0&&<button onClick={e=>{e.stopPropagation();onApply(bestParamsToApplyData(t.params));}}
                          style={{fontSize:11,padding:"3px 8px",borderRadius:4,border:`1px solid rgba(37,99,235,0.2)`,background:theme.blueLight,color:theme.blueText,cursor:"pointer",fontWeight:700}}>Apply</button>}
                      </div>
                    </td>}
                  </tr>,
                  // DQ reason row
                  t.is_disqualified && !trialHideDQ && dqReasons.length > 0 &&
                  <tr key={`${t.trial}-dq`} style={{background:"rgba(239,68,68,0.04)",borderBottom:`1px solid ${theme.divider}`}}>
                    <td colSpan={onApply?11:10} style={{padding:"3px 10px 5px 28px"}}>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {dqReasons.slice(0,5).map((r,i)=>(
                          <span key={i} style={{fontSize:10,padding:"1px 7px",borderRadius:3,background:"rgba(239,68,68,0.1)",color:theme.redText,fontFamily:fonts.mono}}>⛔ {r}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ];
              })}</tbody>
            </table>
          </div>
          {totalShown > trialPageSize && (
            <div style={{padding:"10px 16px",display:"flex",gap:8,justifyContent:"flex-end",borderTop:`1px solid ${theme.divider}`}}>
              <button onClick={()=>setTrialPageSize(s=>s+250)}
                style={{padding:"6px 14px",fontSize:12,borderRadius:6,border:`1px solid ${theme.border}`,background:theme.white,color:theme.text2,cursor:"pointer"}}>+250 mehr</button>
              <button onClick={()=>setTrialPageSize(totalShown)}
                style={{padding:"6px 14px",fontSize:12,borderRadius:6,border:`1px solid ${theme.blueText}`,background:theme.blueLight,color:theme.blueText,cursor:"pointer",fontWeight:600}}>Alle ({totalShown})</button>
            </div>
          )}
        </Card>;
      })()}
    </>}

    {tab==="params" && trials.length > 0 && <ParamsTab trials={trials}/>}

        {tab==="analysis" && <>
      <Card style={{ marginBottom:14 }}>
        <CardHeader title="📊 Aggregate · alle Top-30 Consensus-Trials" sub="Kombinierte Stats · Erkenntnisse · Trial-Vergleichstabelle"/>
        <div style={{ padding:"14px 20px" }}>
          <TradeAggregatePanel runType="opt" runId={runId}/>
        </div>
      </Card>
      <Card>
        <CardHeader title="🔍 Einzel-Trial Trade Analysis" sub="Trial-Picker · Summary · Equity · Charts · Tabelle"/>
        <div style={{ padding:"14px 20px" }}>
          <TradeAnalysisPanel runType="opt" runId={runId}/>
        </div>
      </Card>
    </>}

    {tab==="regime" && <Card>
      <CardHeader title="🌊 MTF Regime Analyse" sub="Daily- und H1-Trend-Phasen mit Trade-Overlay · diagnostiziert Filter-Kalibrierung"/>
      <div style={{ padding:"14px 20px" }}>
        <RegimeTab data={data}/>
      </div>
    </Card>}

    {tab==="pareto" && isPareto && <>
      <Card style={{ marginBottom:14 }}>
        <CardHeader
          title="🎯 Pareto-Front — Effizienzgrenze"
          sub={`${paretoFront.length} nicht-dominierte Trials · ${paretoAxes[0]} vs ${paretoAxes[1]}`}
        />
        <div style={{ padding:"16px 20px" }}>
          {/* Explainer */}
          <div style={{ padding:"10px 14px", marginBottom:16, borderRadius:8, background:theme.blueLight, fontSize:12, color:theme.blueText, lineHeight:1.6 }}>
            <b>Was ist die Pareto-Front?</b> Jeder Punkt ist ein Trial der in mindestens einer Dimension besser ist als alle anderen.
            Kein Punkt dominiert einen anderen — du wählst bewusst deinen Trade-off zwischen Return und Drawdown.
            Klick auf einen Punkt um die Parameter zu sehen.
          </div>
          <ParetoChart
            front={paretoFront}
            ax1Label={paretoAxes[0]}
            ax2Label={paretoAxes[1]}
            selected={selPareto}
            onSelect={d => setSelPareto(prev => prev?.trial === d.trial ? null : d)}
          />
        </div>
      </Card>

      {/* Selected trial detail */}
      {selPareto && <Card style={{ marginBottom:14, border:`2px solid ${theme.blue}` }}>
        <CardHeader
          title={`Trial #${selPareto.trial} — Pareto-Punkt`}
          sub={`Grade ${selPareto.grade} · P&L ${formatPnl(selPareto.net_pnl)} · DD ${selPareto.max_drawdown?.toFixed(1)}% · WR ${selPareto.win_rate?.toFixed(1)}%`}
          right={<>
            {onApply && selPareto.params && <Button primary onClick={() => onApply(bestParamsToApplyData(selPareto.params))} style={{ marginRight:8 }}>Apply diesen Punkt</Button>}
            <Button onClick={() => setSelPareto(null)}>Schließen</Button>
          </>}
        />
        <div style={{ padding:"14px 20px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10, marginBottom:14 }}>
            {[
              ["Return", formatPnl(selPareto.net_pnl), selPareto.net_pnl >= 0 ? theme.greenText : theme.redText],
              ["Max DD", `${selPareto.max_drawdown?.toFixed(1)}%`, theme.redText],
              ["Win Rate", `${selPareto.win_rate?.toFixed(1)}%`, theme.text],
              ["Sharpe", selPareto.sharpe?.toFixed(2), theme.blueText],
              ["Total Trades", selPareto.total_trades, theme.text],
              ["Grade", selPareto.grade, selPareto.grade==="A"?theme.greenText:theme.blueText],
            ].map(([l,v,c]) => (
              <div key={l} style={{ padding:"10px 14px", borderRadius:8, background:theme.tint }}>
                <div style={{ fontSize:10, color:theme.text3, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:16, fontWeight:800, fontFamily:fonts.mono, color:c }}>{v}</div>
              </div>
            ))}
          </div>
          {selPareto.params && Object.keys(selPareto.params).length > 0 && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:theme.text3, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:8 }}>Parameter</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:4 }}>
                {Object.entries(selPareto.params).map(([k,v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"4px 10px", borderRadius:5, background:theme.tint }}>
                    <span style={{ fontSize:11, color:theme.text2 }}>{k.replace(/_/g," ")}</span>
                    <span style={{ fontSize:11, fontWeight:700, fontFamily:fonts.mono, color:typeof v==="boolean"?(v?theme.greenText:theme.redText):theme.text }}>
                      {typeof v==="number"?v.toFixed(2):String(v)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>}

      {/* All pareto-front trials as table */}
      <Card>
        <CardHeader title="Alle Pareto-Front Trials" sub={`${paretoFront.length} nicht-dominierte Trials`}/>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`2px solid ${theme.divider}` }}>
                {["Trial","Grade","P&L","Max DD","Win Rate","Sharpe","Trades",paretoAxes[0],paretoAxes[1],""].map(h => (
                  <th key={h} style={{ padding:"6px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paretoFront.map((d,i) => (
                <tr key={i} className="sl-row"
                  style={{ borderBottom:`1px solid ${theme.divider}`, cursor:"pointer",
                           background: selPareto?.trial===d.trial ? theme.blueLight : "transparent" }}
                  onClick={() => setSelPareto(prev => prev?.trial===d.trial ? null : d)}>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono, fontWeight:700 }}>#{d.trial}</td>
                  <td style={{ padding:"5px 10px" }}><Pill bg={d.grade==="A"?theme.greenLight:theme.blueLight} color={d.grade==="A"?theme.greenText:theme.blueText}>{d.grade}</Pill></td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono, color:d.net_pnl>=0?theme.greenText:theme.redText, fontWeight:700 }}>{formatPnl(d.net_pnl)}</td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono, color:theme.redText }}>{d.max_drawdown?.toFixed(1)}%</td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono }}>{d.win_rate?.toFixed(1)}%</td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono }}>{d.sharpe?.toFixed(2)}</td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono }}>{d.total_trades}</td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono, color:theme.blueText }}>{d.ax1_val?.toFixed(2)}</td>
                  <td style={{ padding:"5px 10px", fontSize:11, fontFamily:fonts.mono, color:theme.amberText }}>{d.ax2_val?.toFixed(2)}</td>
                  <td style={{ padding:"5px 6px" }}>
                    {onApply && d.params && <Button onClick={e => { e.stopPropagation(); onApply(bestParamsToApplyData(d.params)); }} style={{ fontSize:10, padding:"3px 8px" }}>Apply</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>}
  </div>;
}

/* ═══ WALK-FORWARD DETAIL ════════════════════════ */
export function WFDetail({ runId, onClose }) {
  const [data,setData]=useState(null); const [ld,setLd]=useState(true);
  const [wfFilter, setWfFilter] = useState({ minOosPnl:"", maxOosDd:"", minOosPf:"", minOosSharpe:"" });
  useEffect(() => { if(!runId) return; setLd(true); getApi(`/results/walkforwards/${runId}`).then(d => { setData(d); setLd(false); }); }, [runId]);
  if (ld) return <SkeletonCards n={4}/>;
  if (!data) return <Card style={{ padding:32, textAlign:"center" }}><div style={{ color:theme.redText }}>Not found</div><Button onClick={onClose} style={{ marginTop:12 }}>Back</Button></Card>;
  const wins = data.windows||[]; const sum = data.summary||{};

  /* ── v4 Walk-Forward Interpretation — trusts backend summary.robustness ── */
  const wfInsights = (() => {
    const totalWins = sum.total_windows || wins.length;
    const profWins = sum.profitable_windows || wins.filter(w => (w.oos_pnl||0) > 0).length;
    const totalOosPnl = sum.total_oos_pnl || 0;
    const ins = [];

    // Backend delivers authoritative verdict via sum.robustness: ROBUST / FRAGILE / UNRELIABLE / LOSING_OOS
    const robustness = sum.robustness || "UNKNOWN";
    const violations = sum.constraint_violations || [];

    // Primary verdict message
    if (robustness === "ROBUST") {
      ins.push({icon:"🚀",text:`ROBUST — alle OOS-Constraints erfüllt. ${profWins}/${totalWins} Windows profitabel, Total OOS ${formatPnl(totalOosPnl)}.`,color:theme.greenText});
      if (sum.aggregate_degradation_pct !== undefined) {
        ins.push({icon:"📊",text:`IS→OOS Degradation: aggregate ${sum.aggregate_degradation_pct}%, median per-window ${sum.median_window_degradation_pct}%.`});
      }
      if (sum.cross_instrument) {
        const xi = sum.cross_instrument;
        ins.push({icon:"🔀",text:`Cross-Instrument: ${xi.profitable_count}/${xi.instruments_tested} profitabel ${xi.passed ? "(✓ passed)" : "(✗ failed)"}.`, color: xi.passed ? theme.greenText : theme.redText});
      }
    } else if (robustness === "FRAGILE") {
      ins.push({icon:"⚠️",text:`FRAGILE — ${violations.length} Constraint-Violation(s).`,color:theme.redText});
      violations.forEach(v => ins.push({icon:"•",text:v,color:theme.text2}));
    } else if (robustness === "UNRELIABLE") {
      ins.push({icon:"📊",text:`UNRELIABLE — nur ${sum.reliable_windows_pct}% reliable Windows (benötigt ≥60%).`,color:theme.amberText});
    } else if (robustness === "LOSING_OOS") {
      ins.push({icon:"❌",text:`LOSING OOS — Total OOS PnL ${formatPnl(totalOosPnl)}.`,color:theme.redText});
    } else {
      ins.push({icon:"❓",text:`Robustness-Label: ${robustness}`,color:theme.text3});
    }

    // Mapping to UI-legacy fields (für zurückwärtskompatible Pill-Anzeige)
    const myVerdict = robustness === "ROBUST" ? "SOLIDE" :
                      robustness === "FRAGILE" ? "FRAGIL" :
                      robustness === "UNRELIABLE" ? "UNRELIABLE" : "UNGENÜGEND";
    const totalScore = robustness === "ROBUST" ? 15 :
                       robustness === "FRAGILE" ? 8 :
                       robustness === "UNRELIABLE" ? 6 : 3;

    return { ins, myVerdict, totalScore };
  })();
  const wfInsightsList = wfInsights.ins;

  return <div>
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
      <BackBtn onClick={onClose}/>
      <div>
        <div style={{ fontSize:18, fontWeight:900 }}>Walk-Forward #{runId} <span style={{ fontSize:12, fontWeight:500, color:theme.text3 }}>{data.instrument||""}</span></div>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:3 }}>
          <span style={{ fontSize:12, color:theme.text3, fontFamily:fonts.mono }}>{wins.length} windows</span>
          {data.strategy_file && <span style={{ fontSize:11, color:theme.text4, fontFamily:fonts.mono }}>{data.strategy_file}</span>}
        </div>
      </div>
      {/* R7r-3: Show honest verdict alongside backend label */}
      <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center" }}>
        {sum.robustness && <Pill bg={theme.tint} color={theme.text3} style={{ fontSize:10, fontWeight:700, padding:"3px 10px" }}>Label: {sum.robustness}</Pill>}
        <Pill
          bg={wfInsights.myVerdict==="SOLIDE"?theme.greenLight:wfInsights.myVerdict==="AKZEPTABEL"?theme.amberLight:wfInsights.myVerdict==="FRAGIL"?theme.amberLight:theme.redLight}
          color={wfInsights.myVerdict==="SOLIDE"?theme.greenText:wfInsights.myVerdict==="AKZEPTABEL"?theme.amberText:wfInsights.myVerdict==="FRAGIL"?theme.amberText:theme.redText}
          style={{ fontSize:13, fontWeight:800, padding:"6px 18px" }}>
          {wfInsights.myVerdict} ({wfInsights.totalScore}/15)
        </Pill>
      </div>
    </div>

    {/* Interpretation Panel */}
    <Card style={{ marginBottom:18, padding:"18px 22px", background:wfInsights.myVerdict==="SOLIDE"?"rgba(22,163,74,0.03)":wfInsights.myVerdict==="AKZEPTABEL"?"rgba(245,158,11,0.03)":"rgba(239,68,68,0.03)", border:`1px solid ${wfInsights.myVerdict==="SOLIDE"?"rgba(22,163,74,0.15)":wfInsights.myVerdict==="AKZEPTABEL"?"rgba(245,158,11,0.15)":"rgba(239,68,68,0.15)"}` }} className="sl-fadein">
      <div style={{ fontSize:15, fontWeight:800, color:theme.text, marginBottom:12 }}>📋 Walk-Forward Analyse · Multi-Dimensional Scoring</div>
      {wfInsightsList.map((ins,i)=><div key={i} style={{ display:"flex", gap:10, padding:"7px 0", borderBottom:i<wfInsightsList.length-1?`1px solid ${theme.divider}`:"none" }}>
        <span style={{ fontSize:15, flexShrink:0 }}>{ins.icon}</span>
        <span style={{ fontSize:13, color:ins.color||theme.text2, lineHeight:1.6 }}>{ins.text}</span>
      </div>)}
    </Card>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:8, marginBottom:18 }}>
      {[["Total OOS P&L",formatPnl(sum.total_oos_pnl),(sum.total_oos_pnl||0)>0?theme.greenText:theme.redText],["Reliable Windows",`${sum.reliable_windows||0}/${sum.total_windows||0}`,(sum.reliable_windows_pct||0)>=60?theme.greenText:theme.amberText],["Profitable (rel.)",`${sum.window_win_rate||0}%`,(sum.window_win_rate||0)>60?theme.greenText:theme.redText],["Aggregate Degrad.",sum.aggregate_degradation_pct!=null?`${sum.aggregate_degradation_pct}%`:"—",(sum.aggregate_degradation_pct||0)<-50?theme.redText:(sum.aggregate_degradation_pct||0)<-30?theme.amberText:theme.greenText],["Median Win Degr.",sum.median_window_degradation_pct!=null?`${sum.median_window_degradation_pct}%`:"—",theme.text3],["Robustness",sum.robustness||"—",sum.robustness==="ROBUST"?theme.greenText:theme.redText]].map(([l,v,c]) =>
        <Card key={l} className="sl-card sl-fadein" style={{ padding:"10px 14px" }}><div style={{ fontSize:8.5, fontWeight:700, color:theme.text4, textTransform:"uppercase", letterSpacing:".6px", marginBottom:4 }}>{l}</div><div style={{ fontSize:15, fontWeight:800, fontFamily:fonts.mono, color:c, lineHeight:1 }}>{v}</div></Card>
      )}
    </div>
    {wins.length > 0 && <><SectionTitle>IS vs OOS Performance</SectionTitle><Card style={{ padding:"12px 16px", marginBottom:18 }}><WFBars windows={wins}/></Card></>}
    <SectionTitle>Window Breakdown</SectionTitle>
    <Card><div style={{ overflow:"auto" }}>
      {/* WF Filter Row */}
      <div style={{ display:"flex", gap:8, alignItems:"center", padding:"10px 14px", borderBottom:`1px solid ${theme.divider}`, flexWrap:"wrap" }}>
        {[
          ["≥ Min OOS P&L","minOosPnl","50.000"],
          ["≤ Max DD%","maxOosDd","10"],
          ["≥ Min PF","minOosPf","1.3"],
          ["≥ Min Sharpe","minOosSharpe","1.5"],
        ].map(([label,field,ph]) => (
          <div key={field} style={{ display:"flex", flexDirection:"column", gap:2 }}>
            <div style={{ fontSize:10, color:wfFilter[field]?theme.blueText:theme.text4, fontWeight:600, textTransform:"uppercase" }}>{label}</div>
            <input type="number" placeholder={ph} value={wfFilter[field]}
              onChange={e => setWfFilter(p=>({...p,[field]:e.target.value}))}
              onKeyDown={e=>e.stopPropagation()} onKeyUp={e=>e.stopPropagation()}
              style={{ width:90, padding:"5px 8px", borderRadius:6, border:`1.5px solid ${wfFilter[field]?theme.blueText:theme.border}`, fontSize:12, fontFamily:fonts.mono, background:wfFilter[field]?"rgba(37,99,235,0.04)":theme.white, color:theme.text, outline:"none" }}/>
          </div>
        ))}
        {Object.values(wfFilter).some(v=>v!=="") && (
          <button onClick={()=>setWfFilter({minOosPnl:"",maxOosDd:"",minOosPf:"",minOosSharpe:""})}
            style={{ alignSelf:"flex-end", padding:"5px 10px", fontSize:11, borderRadius:6, border:`1px solid ${theme.border}`, background:theme.tint, color:theme.text3, cursor:"pointer" }}>✕ Reset</button>
        )}
        <span style={{ marginLeft:"auto", fontSize:11, color:theme.text3 }}>
          {wins.filter(w => {
            const f = wfFilter;
            if (f.minOosPnl && (w.oos_pnl||0) < Number(f.minOosPnl)) return false;
            if (f.maxOosDd  && (w.oos_max_dd||0) > Number(f.maxOosDd))  return false;
            if (f.minOosPf  && (w.oos_pf||0)  < Number(f.minOosPf))  return false;
            if (f.minOosSharpe && (w.oos_sharpe||0) < Number(f.minOosSharpe)) return false;
            return true;
          }).length} / {wins.length} Windows
        </span>
      </div>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead><tr style={{ borderBottom:`2px solid ${theme.divider}` }}>{["W","IS Period","OOS Period","IS P&L","OOS P&L","IS PF","OOS PF","OOS WR","OOS Sharpe","Trades","Params"].map(h => <th key={h} style={{ padding:"8px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left", whiteSpace:"nowrap" }}>{h}</th>)}</tr></thead>
        <tbody>{wins.filter(w => {
          const f = wfFilter;
          if (f.minOosPnl && (w.oos_pnl||0) < Number(f.minOosPnl)) return false;
          if (f.maxOosDd  && (w.oos_max_dd||0) > Number(f.maxOosDd))  return false;
          if (f.minOosPf  && (w.oos_pf||0)  < Number(f.minOosPf))  return false;
          if (f.minOosSharpe && (w.oos_sharpe||0) < Number(f.minOosSharpe)) return false;
          return true;
        }).map(w => {
          const p = w.best_params || {};
          const hasParms = Object.keys(p).length > 0;
          const paramTags = hasParms ? [
            p.sl_atr_mult         != null && `SL ${Number(p.sl_atr_mult).toFixed(2)}×`,
            p.atr_floor_mult      != null && `Floor ${Number(p.atr_floor_mult).toFixed(2)}×`,
            p.tp1_rr              != null && `TP1 ${Number(p.tp1_rr).toFixed(1)}R`,
            p.tp2_rr              != null && p.tp2_rr > 0 && `TP2 ${Number(p.tp2_rr).toFixed(1)}R`,
            p.partial_pcts        != null && `Split ${p.partial_pcts}`,
            p.be_after_rr         != null && `BE @${Number(p.be_after_rr).toFixed(2)}R`,
            p.trailing_atr_mult   != null && `Trail ${Number(p.trailing_atr_mult).toFixed(2)}×`,
            p.trailing_activation_rr != null && `T-Act ${Number(p.trailing_activation_rr).toFixed(2)}R`,
            p.entry_swing_length  != null && `ESw${p.entry_swing_length}`,
            p.context_swing_length!= null && `CSw${p.context_swing_length}`,
            p.max_pattern_age     != null && `Age${p.max_pattern_age}`,
          ].filter(Boolean) : [];
          return <>
            <tr key={w.window} className="sl-row" style={{ borderBottom:`1px solid ${theme.divider}` }}>
              <td style={{ padding:"6px 10px", fontSize:11, fontWeight:700, fontFamily:fonts.mono }}>W{w.window}</td>
              <td style={{ padding:"6px 10px", fontSize:10, fontFamily:fonts.mono, color:theme.text3 }}>{w.is_start?.slice(5)}→{w.is_end?.slice(5)}</td>
              <td style={{ padding:"6px 10px", fontSize:10, fontFamily:fonts.mono, color:theme.text3 }}>{w.oos_start?.slice(5)}→{w.oos_end?.slice(5)}</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontWeight:700, fontFamily:fonts.mono, color:theme.blueText }}>{formatPnl(w.is_pnl)}</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontWeight:700, fontFamily:fonts.mono, color:w.oos_pnl>=0?theme.greenText:theme.redText }}>{formatPnl(w.oos_pnl)}</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{Number(w.is_pf||0).toFixed(2)}</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono, color:w.oos_pf>1.3?theme.greenText:theme.text }}>{Number(w.oos_pf||0).toFixed(2)}</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{w.oos_wr?.toFixed(1)}%</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{Number(w.oos_sharpe||0).toFixed(2)}</td>
              <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{w.oos_trades||0}</td>
              <td style={{ padding:"6px 10px" }}>
                {paramTags.length > 0
                  ? <div style={{ display:"flex", flexWrap:"wrap", gap:3 }}>
                      {paramTags.map((tag,i) => <span key={i} style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:"rgba(37,99,235,0.07)", color:theme.blueText, fontFamily:fonts.mono, fontWeight:600, whiteSpace:"nowrap" }}>{tag}</span>)}
                    </div>
                  : <span style={{ fontSize:9, color:theme.text4 }}>—</span>
                }
              </td>
            </tr>
          </>;
        })}</tbody>
      </table>
    </div></Card>

    <Card style={{ marginTop:14 }}>
      <CardHeader title="📊 Aggregate · alle Windows" sub="Kombinierte Stats · Erkenntnisse · Windows-Vergleichstabelle · IS/OOS-Toggle"/>
      <div style={{ padding:"14px 20px" }}>
        <TradeAggregatePanel runType="wf" runId={runId}/>
      </div>
    </Card>

    <Card style={{ marginTop:14 }}>
      <CardHeader title="🔍 Einzel-Window Trade Analysis" sub="Window-Picker · Segment-Toggle · Summary · Equity · Charts · Tabelle"/>
      <div style={{ padding:"14px 20px" }}>
        <TradeAnalysisPanel runType="wf" runId={runId}/>
      </div>
    </Card>
  </div>;
}

/* ═══ SCAN DETAIL ═════════════════════════════════ */
export function ScanDetail({ runId, onClose, onApplyTf }) {
  const [data,setData]=useState(null); const [ld,setLd]=useState(true); const [expanded,setExpanded]=useState(null);
  useEffect(() => { if(!runId) return; setLd(true); getApi(`/results/scans/${runId}`).then(d => { setData(d); setLd(false); }); }, [runId]);
  if (ld) return <SkeletonCards n={4}/>;
  if (!data) return <Card style={{ padding:32, textAlign:"center" }}><div style={{ color:theme.redText }}>Not found</div><Button onClick={onClose} style={{ marginTop:12 }}>Back</Button></Card>;
  const res = (data.results||[]).sort((a,b) => (b.composite_score||b.score||0)-(a.composite_score||a.score||0));
  return <div>
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
      <BackBtn onClick={onClose}/>
      <div>
        <div style={{ fontSize:18, fontWeight:900 }}>Scan — {data.instrument} <span style={{ fontSize:12, fontWeight:500, color:theme.text3 }}>#{runId}</span></div>
        <div style={{ fontSize:11, color:theme.text3, fontFamily:fonts.mono }}>{data.start_date} → {data.end_date} · {res.length} combos</div>
      </div>
    </div>
    {onApplyTf && <div style={{ fontSize:11, color:theme.text3, marginBottom:14 }}>Klicke <span style={{ color:theme.blueText, fontWeight:600 }}>"Apply TF"</span> um Timeframes in die Strategy-YAML zu übernehmen.</div>}
    <Card><div style={{ overflow:"auto", maxHeight:700 }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead><tr style={{ borderBottom:`2px solid ${theme.divider}`, position:"sticky", top:0, background:theme.white, zIndex:1 }}>
          {["Rank","Label","Grade","Score","Net P&L","Sharpe","PF","WR","Trades","Max DD",""].map(h => <th key={h} style={{ padding:"8px 10px", fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:h==="Rank"?"center":"left" }}>{h}</th>)}
        </tr></thead>
        <tbody>{res.map((r,i) => {
          const sc=r.composite_score||r.score||0, m=r.metrics||r, pnl=m.net_pnl||0;
          const gd=r.grade||(sc>0.8?"A":sc>0.65?"B":sc>0.5?"C":sc>0.35?"D":"F");
          const isExp = expanded === i; const dims = r.dimensions||{}; const flags = r.flags||[];
          return <><tr key={i} className="sl-row" style={{ borderBottom:`1px solid ${theme.divider}`, background:i===0?theme.greenLight:"transparent", cursor:"pointer" }} onClick={() => setExpanded(isExp?null:i)}>
            <td style={{ padding:"6px 10px", textAlign:"center" }}>{i<3?<span style={{fontSize:14}}>{["🥇","🥈","🥉"][i]}</span>:<span style={{fontSize:11,color:theme.text4,fontFamily:fonts.mono}}>{i+1}</span>}</td>
            <td style={{ padding:"6px 10px", fontSize:12, fontWeight:700, fontFamily:fonts.mono }}>{r.label||"?"}</td>
            <td style={{ padding:"6px 10px" }}><Pill bg={gd==="A"?theme.greenLight:gd==="B"?theme.blueLight:gd==="C"?theme.amberLight:theme.redLight} color={gd==="A"?theme.greenText:gd==="B"?theme.blueText:gd==="C"?theme.amberText:theme.redText}>{gd}</Pill></td>
            <td style={{ padding:"6px 10px" }}><span style={{ fontSize:11, fontWeight:800, fontFamily:fonts.mono, color:sc>0.7?theme.greenText:sc>0.4?theme.amberText:theme.redText }}>{(sc*100).toFixed(0)}</span></td>
            <td style={{ padding:"6px 10px", fontSize:11.5, fontWeight:700, fontFamily:fonts.mono, color:pnl>=0?theme.greenText:theme.redText }}>{formatPnl(pnl)}</td>
            <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{Number(m.sharpe||0).toFixed(2)}</td>
            <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{Number(m.profit_factor||0).toFixed(2)}</td>
            <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{typeof m.win_rate==="number"?(m.win_rate>1?m.win_rate:(m.win_rate*100)).toFixed(1):0}%</td>
            <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono }}>{m.total_trades||m.trades||0}</td>
            <td style={{ padding:"6px 10px", fontSize:11, fontFamily:fonts.mono, color:theme.redText }}>{Number(m.max_drawdown||0).toFixed(1)}%</td>
            <td style={{ padding:"6px 10px" }}>{onApplyTf && r.label && <button onClick={e => { e.stopPropagation(); onApplyTf(r.label); }} className="sl-btn" style={{ fontSize:9, padding:"4px 10px", borderRadius:5, border:`1px solid ${i===0?"rgba(22,163,74,0.3)":theme.border}`, background:i===0?theme.greenLight:theme.white, color:i===0?theme.greenText:theme.blueText, cursor:"pointer", fontWeight:700, whiteSpace:"nowrap" }}>Apply TF</button>}</td>
          </tr>
          {isExp && <tr key={`exp-${i}`}><td colSpan={11} style={{ padding:"12px 16px", background:"rgba(0,0,0,0.015)", borderBottom:`1px solid ${theme.divider}` }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:8 }}>
              {[["Profitability",dims.profitability],["Risk Control",dims.risk_control],["Consistency",dims.consistency],["Reliability",dims.reliability]].map(([n,v]) =>
                <div key={n}><div style={{ fontSize:9, fontWeight:700, color:theme.text4, textTransform:"uppercase", marginBottom:3 }}>{n}</div><div style={{ height:5, borderRadius:3, background:theme.tint, marginBottom:3 }}><div style={{ height:"100%", borderRadius:3, background:(v||0)>0.7?theme.green:(v||0)>0.4?theme.amber:theme.red, width:`${(v||0)*100}%` }}/></div><div style={{ fontSize:11, fontWeight:700, fontFamily:fonts.mono, color:(v||0)>0.7?theme.greenText:(v||0)>0.4?theme.amberText:theme.redText }}>{((v||0)*100).toFixed(0)}%</div></div>
              )}
            </div>
            {flags.length > 0 && <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>{flags.map((f,fi) => <span key={fi} style={{ fontSize:10, padding:"3px 8px", borderRadius:5, background:f.startsWith("✓")?theme.greenLight:theme.amberLight, color:f.startsWith("✓")?theme.greenText:theme.amberText }}>{f}</span>)}</div>}
          </td></tr>}
          </>;
        })}</tbody>
      </table>
    </div></Card>
  </div>;
}
